import { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { useDocumentTitle } from '@/utils/use-document-title';
import { getActorId } from '@/utils/http';
import * as api from '@/features/canvas/api';
import { Camera, CanvasElement, ElementStyle, ShapeVariant, Whiteboard } from '@/features/canvas/types';
import { useWhiteboardRealtime } from '@/features/canvas/realtime';
import { Spinner } from '@/components/ui/Spinner';
import ContextMenu, { MenuEntry } from '@/components/ui/ContextMenu';
import MemberAvatar from '@/components/member-avatar';
import { fmtDateTime, timeAgo } from '@/features/backlog/format';
import { Duplicate, ToBottom, ToTop, Trash } from '@/components/ui/icons';
import {
  angleTo,
  boundsOf,
  computeResize,
  computeSnap,
  connectorGeometry,
  elementCenter,
  freehandHit,
  Handle,
  hitTest,
  hitTestMargin,
  MIN_SIZE,
  normRect,
  perimeterAnchor,
  projectToPolylineT,
  rectContains,
  ResizeMode,
  resizeRotated,
  screenToWorld,
  SnapLine,
  snapRail,
  worldToScreen,
} from './geometry';
import { useElements } from './use-elements';
import { useCanvasItemsStore, CanvasItemsProvider } from './use-canvas-items';
import ItemCardPicker from './item-card-picker';
import SearchQueryPicker from './search-query-picker';
import { useComments } from './use-comments';
import CommentPins from './comment-pins';
import CommentThreadPopover from './comment-thread';
import CommentFlyout from './comment-flyout';
import Toolbar, { Tool, PenMode } from './toolbar';
import TopBar from './topbar';
import Inspector, { ToolbarAnchor } from './inspector';
import ConnectorInspector from './connector-inspector';
import Cursors from './cursors';
import OverlaySvg, { Draft } from './overlay-svg';
import DraftLayer from './draft-layer';
import PixiInkLayer, { PixiInkHandle } from './pixi-ink';
import PointerDebug from './pointer-debug';
import SelectionLayer from './selection-layer';
import SnapGuidesLayer, { ScreenGuide, SnapGuidesHandle } from './snap-guides';
import ElementView from './elements';
import { ConnectionNubs, TargetHighlight } from './connection-nubs';
import ConnectorHandles from './connector-handles';
import ConnectorLabel from './connector-label';

type Geometry = { x: number; y: number; w: number; h: number; rotation: number };

type Gesture =
  | { kind: 'idle' }
  | { kind: 'pan'; startScreen: { x: number; y: number }; startCam: { x: number; y: number } }
  | {
      kind: 'move';
      startWorld: { x: number; y: number };
      originals: Record<string, { x: number; y: number; rotation: number }>;
      nodes?: Map<string, HTMLElement>; // dragged DOM nodes, looked up once (imperative translate per frame)
      dx: number;
      dy: number;
      moved: boolean;
    }
  | { kind: 'resize'; id: string; handle: Handle; prev: Geometry; mode: ResizeMode; scaleFont: boolean; fitFont: boolean; origStyle: ElementStyle; origFont: number; widthOnly: boolean }
  | { kind: 'rotate'; id: string; center: { x: number; y: number }; startAngle: number; prevRotation: number }
  | {
      kind: 'groupResize';
      handle: Handle;
      bounds: { x: number; y: number; w: number; h: number };
      originals: Record<string, { x: number; y: number; w: number; h: number; rotation: number; style: ElementStyle; kind: string }>;
      moved: boolean;
    }
  | { kind: 'marquee'; startScreen: { x: number; y: number }; base: string[]; lastKey?: string }
  | { kind: 'pen'; stroke: number[][] }
  | { kind: 'connector'; fromId: string; fromEdge: boolean; startScreen: { x: number; y: number }; startWorld: { x: number; y: number } }
  | { kind: 'create'; startWorld: { x: number; y: number }; tool: Tool }
  | { kind: 'place'; tool: Tool; startScreen: { x: number; y: number }; moved: boolean }
  | { kind: 'waypoint'; id: string; index: number; origStyle: ElementStyle }
  | { kind: 'segment'; id: string; controlIndex: number; orientation: 'h' | 'v' | 'free' | 'railh' | 'railv'; mid: { x: number; y: number }; startScreen: { x: number; y: number }; origStyle: ElementStyle; added: boolean }
  | { kind: 'label'; id: string; samplePts: { x: number; y: number }[]; origStyle: ElementStyle; moved: boolean }
  | { kind: 'endpoint'; id: string; which: 'from' | 'to'; origFromId: string | null; origToId: string | null; origStyle: ElementStyle };

const ZOOM_MIN = 0.02; // 2% — zoom out far enough to see a very large board
const ZOOM_MAX = 4;

/** Gesture kinds that auto-pan the camera when the cursor reaches a viewport edge (Miro-style), so you
 *  can drag/resize/route past the current view. Excludes pan/pen/marquee/rotate/place/create. */
const EDGE_PAN_KINDS = new Set(['move', 'resize', 'groupResize', 'connector', 'endpoint', 'waypoint', 'segment', 'label']);
const EDGE_PAN_ZONE = 52; // px from a viewport edge where auto-pan starts (ramps up toward the edge)
const EDGE_PAN_MAX = 24; // max pan speed (screen px per frame) at/beyond the very edge
const GRID = 24; // world px between grid dots

/** Emit a lifecycle line to the ?ptrdebug overlay (no-op unless the URL flag is set). */
const PTRDEBUG = typeof window !== 'undefined' && window.location.search.includes('ptrdebug');
function dbg(m: string) {
  if (PTRDEBUG) window.dispatchEvent(new CustomEvent('ptrlog', { detail: m }));
}
const DEFAULTS: Record<string, { w: number; h: number }> = {
  sticky: { w: 176, h: 176 },
  shape: { w: 160, h: 110 },
  text: { w: 220, h: 48 },
  item: { w: 280, h: 210 },
  search: { w: 320, h: 220 },
};

const geomOf = (el: CanvasElement): Geometry => ({ x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation });

/** Element kinds a connector can attach to (not other connectors or freehand ink). */
const CONNECTABLE = new Set(['sticky', 'shape', 'text', 'image', 'item', 'search']);

// Custom PAN cursors: a white hand with a black outline, so the cursor stays visible on ANY background.
// The native `grab`/`grabbing` cursors render as an all-white (effectively invisible) glyph on
// Windows/Chrome — hence the SVG data-URI with an explicit black stroke. Open hand = ready to pan,
// closed fist = actively dragging. viewBox is padded to fit the outset stroke; hotspot sits mid-palm.
const GRAB_PATH =
  'M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-176c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 272c0 1.5 0 3.1 .1 4.6L67.6 283c-16-15.2-41.3-14.6-56.6 1.4S-3.6 325.7 12.4 341L124.8 448c43.1 41.1 100.4 64 160 64l19.2 0c97.2 0 176-78.8 176-176l0-208c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-176c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 176c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208z';
const GRABBING_PATH =
  'M7.4 253.6C2.6 245.9 0 237.1 0 228l0-36c0-26.5 21.5-48 48-48l16 0 0-80c0-26.5 21.5-48 48-48 17.3 0 32.4 9.1 40.9 22.8 4.3-22.1 23.8-38.8 47.1-38.8 23.4 0 42.9 16.8 47.1 38.9 7.3-4.4 15.8-6.9 24.9-6.9 22.1 0 40.8 15 46.3 35.4 5.5-2.2 11.4-3.4 17.7-3.4 26.5 0 48 21.5 48 48l0 96.9c0 9.9-2.3 19.7-6.8 28.6l-39.6 79.1c-10.8 21.7-33 35.4-57.2 35.4L96 352c-16.5 0-31.8-8.4-40.6-22.4l-48-76zM32 480l0-48c0-17.7 14.3-32 32-32l256 0c17.7 0 32 14.3 32 32l0 48c0 17.7-14.3 32-32 32L64 512c-17.7 0-32-14.3-32-32z';
const handCursor = (path: string, vbW: number, hotX: number, hotY: number): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="-40 -40 ${vbW + 80} 592"><path d="${path}" fill="#fff" stroke="#000" stroke-width="38" stroke-linejoin="round"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotX} ${hotY}, grab`;
};
const PAN_CURSOR = handCursor(GRAB_PATH, 512, 15, 12);
const PANNING_CURSOR = handCursor(GRABBING_PATH, 384, 13, 14);

/** Element kinds that participate in alignment snapping (boxes with real geometry — not connectors,
 *  whose x/y/w/h are derived and would sit at the origin). */
const SNAP_KINDS = new Set(['sticky', 'shape', 'text', 'image', 'freehand', 'item', 'search']);
const SNAP_PX = 6; // snap/guide threshold in SCREEN px (converted to world by the current zoom)
/** Kinds that resize (a box with real w/h + optional font). Freehand rides a group resize by translation
 *  only; connectors are derived and follow their endpoints. */
const RESIZABLE_KINDS = new Set(['sticky', 'shape', 'text', 'image', 'item', 'search']);
/** Kinds whose label font scales with a proportional (corner) resize — everything resizable except images. */
const FONT_KINDS = new Set(['sticky', 'shape', 'text']);

/** Sticky text padding as a fraction of WIDTH — matches `padding: "9%"` on the sticky's EditableText.
 *  (CSS percentage padding is width-relative on every side, so the inner box is w·(1-2f) × h-2f·w.) */
const STICKY_PAD_FRAC = 0.09;
const MIN_FONT = 6;

/** Offscreen node for measuring how tall a sticky's wrapped text is at a given font/width, so a free
 *  (edge) resize can shrink the font to fit instead of clipping the text. Created lazily, then reused. */
let stickyMeasureEl: HTMLDivElement | null = null;
function stickyMeasure(): HTMLDivElement {
  if (stickyMeasureEl) return stickyMeasureEl;
  const el = document.createElement('div');
  // Mirror the sticky content box: pre-wrap, break long words, 1.25 line-height, inherit the app font.
  // `contain: layout` keeps its forced reflows from cascading into the rest of the document.
  el.style.cssText =
    'position:absolute;left:-99999px;top:0;visibility:hidden;pointer-events:none;box-sizing:border-box;' +
    'white-space:pre-wrap;overflow-wrap:break-word;word-break:normal;line-height:1.25;contain:layout;';
  document.body.appendChild(el);
  stickyMeasureEl = el;
  return el;
}

/**
 * Largest font size ≤ maxFont (and ≥ MIN_FONT) at which `content` wraps to fit within an innerW × innerH
 * content box. Returns maxFont when the text already fits (or is empty). Lets a sticky shrink its text to
 * fit when dragged smaller, instead of clipping.
 */
function fitStickyFont(content: string, innerW: number, innerH: number, maxFont: number, bold?: boolean): number {
  if (!content || innerW <= 1 || innerH <= 1) return Math.max(MIN_FONT, Math.min(maxFont, Math.round(Math.max(1, innerH))));
  const m = stickyMeasure();
  m.style.width = `${innerW}px`;
  m.style.fontWeight = bold ? '700' : '400';
  m.textContent = content;
  const fits = (f: number) => {
    m.style.fontSize = `${f}px`;
    return m.scrollHeight <= innerH + 0.5;
  };
  if (fits(maxFont)) return maxFont;
  let lo = MIN_FONT;
  let hi = maxFont;
  for (let i = 0; i < 7; i += 1) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return Math.max(MIN_FONT, Math.floor(lo));
}

/** Convert a world-space alignment guide to screen coords for the SnapGuidesLayer. */
function guideToScreen(cam: Camera, g: SnapLine): ScreenGuide {
  if (g.axis === 'x') return { axis: 'x', at: g.at * cam.zoom + cam.x, from: g.from * cam.zoom + cam.y, to: g.to * cam.zoom + cam.y };
  return { axis: 'y', at: g.at * cam.zoom + cam.y, from: g.from * cam.zoom + cam.x, to: g.to * cam.zoom + cam.x };
}

const camKey = (id: string) => `wb:cam:${id}`;

/** A shareable/persisted view = the WORLD point at the viewport centre + zoom, so the same view is
 *  reproduced regardless of the viewer's window size (they land near the same spot, same zoom). */
function viewParams(zoom: number, center: { x: number; y: number }): string {
  const p = new URLSearchParams();
  p.set('x', center.x.toFixed(1));
  p.set('y', center.y.toFixed(1));
  p.set('z', zoom.toFixed(4));
  return p.toString();
}
/** Build a camera that puts world (x,y) at the viewport centre (screen = world·z + cam ⇒ cam = vpc − world·z). */
function camFromViewParams(search: string, vp: { w: number; h: number }): Camera | null {
  const p = new URLSearchParams(search);
  const x = parseFloat(p.get('x') ?? '');
  const y = parseFloat(p.get('y') ?? '');
  const z = parseFloat(p.get('z') ?? '');
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z) || z <= 0) return null;
  return { zoom: z, x: vp.w / 2 - x * z, y: vp.h / 2 - y * z };
}

/** Base ink width (world px); the pen thins/thickens it from pointer pressure. */
const PEN_SIZE = 8;
/** Highlighter renders wider than the chosen pen width so it reads as a marker. */
const HIGHLIGHTER_SCALE = 2;
/** A tiny floor so the ribbon never necks to literally nothing, but light strokes still read as thin. */
const PEN_PRESSURE_FLOOR = 0.05;
const clampPressure = (p: number) => Math.max(PEN_PRESSURE_FLOOR, Math.min(1, p));
/** EMA weight on the PREVIOUS sample's pressure — light, so it clips per-sample spikes without
 *  flattening the expressive pressure signal (deliberate press changes are lower-frequency, pass through). */
const PEN_PRESSURE_SMOOTH = 0.2;

/** Wheel/pinch → zoom: how strongly scroll-delta maps to a zoom factor (factor = e^(-delta·k)).
 *  Lower = gentler. Tuned so macOS trackpad pinch / ⌘+scroll feel smooth, not jumpy. */
const ZOOM_WHEEL_SENSITIVITY = 0.0034;
/** Extra gain applied to PINCH zoom (touch pinch, trackpad pinch, Safari gesture) — a pinch zooms this
 *  much more per gesture. Applied as an exponent on the pinch scale so scale=1 stays identity and both
 *  zoom-in and zoom-out amplify symmetrically. */
const PINCH_ZOOM_GAIN = 1.596; // 1.33 × 1.20
/** Per-frame catch-up toward the target zoom (0..1). Lower = more easing/inertia. */
const ZOOM_EASE = 0.26;
/** Per-frame easing toward a followed peer's camera — lower = smoother/slower glide (no snapping). */
const FOLLOW_EASE = 0.16;

export default function CanvasPage() {
  const { whiteboardId = '' } = useParams();
  const navigate = useNavigate();
  const { me } = useIdentity();
  const { members } = useAppData();
  const clipboardRef = useRef<CanvasElement[]>([]);

  const selfId = me?.id ?? getActorId() ?? '';
  const selfName = me?.name ?? 'You';
  const selfColor = me?.color ?? '#2ec5e8';

  const [wb, setWb] = useState<Whiteboard | null>(null);
  useDocumentTitle(wb?.name);
  const [loading, setLoading] = useState(true);

  // Camera (world→screen = world*zoom + {x,y}); a ref mirror lets gesture handlers read it live.
  const [cam, setCamState] = useState<Camera>({ x: 0, y: 0, zoom: 1 });
  const camRef = useRef(cam);
  const setCam = useCallback((u: Camera | ((c: Camera) => Camera)) => {
    setCamState((prev) => {
      const next = typeof u === 'function' ? (u as (c: Camera) => Camera)(prev) : u;
      camRef.current = next;
      return next;
    });
  }, []);
  // Viewport size (for virtualizing DOM elements to what's actually on screen).
  const [vpSize, setVpSize] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1280, h: typeof window !== 'undefined' ? window.innerHeight : 800 });
  useEffect(() => {
    const onResize = () => setVpSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [tool, setToolState] = useState<Tool>('select');
  const toolRef = useRef(tool);
  const setTool = useCallback((t: Tool) => {
    toolRef.current = t;
    setToolState(t);
  }, []);

  const [color, setColor] = useState('#4dabf7');
  const colorRef = useRef(color);
  colorRef.current = color;

  // Per-context colours: shapes/stickies/text use `color` (main); the pen and the highlighter each keep
  // their own last-used colour, so switching pen↔marker↔other doesn't clobber the others.
  const [penColor, setPenColorState] = useState('#4dabf7');
  const penColorRef = useRef(penColor);
  const setPenColor = useCallback((c: string) => {
    penColorRef.current = c;
    setPenColorState(c);
  }, []);
  const [markerColor, setMarkerColorState] = useState('#f6b91a');
  const markerColorRef = useRef(markerColor);
  const setMarkerColor = useCallback((c: string) => {
    markerColorRef.current = c;
    setMarkerColorState(c);
  }, []);

  const [shapeVariant, setShapeVariantState] = useState<ShapeVariant>('rectangle');
  const shapeVariantRef = useRef(shapeVariant);
  const setShapeVariant = useCallback((v: ShapeVariant) => {
    shapeVariantRef.current = v;
    setShapeVariantState(v);
  }, []);

  const [penSize, setPenSizeState] = useState(PEN_SIZE);
  const penSizeRef = useRef(penSize);
  const setPenSize = useCallback((s: number) => {
    penSizeRef.current = s;
    setPenSizeState(s);
  }, []);

  // Pen vs highlighter (a translucent, wider, uniform-width marker). Kept as a mode of the pen tool.
  const [penMode, setPenModeState] = useState<PenMode>('pen');
  const penModeRef = useRef(penMode);
  const setPenMode = useCallback((m: PenMode) => {
    penModeRef.current = m;
    setPenModeState(m);
  }, []);
  // True while a move/resize/rotate gesture is in flight — hides the floating toolbar (its backdrop-filter
  // repaints per frame otherwise, another Windows/Chrome drag-lag source) and reappears on release.
  const [interacting, setInteracting] = useState(false);
  // True while a pan gesture is dragging (hand tool, space-pan, or middle-button) — swaps the open-hand
  // cursor for a closed fist. Reset in up() (which always runs on pointerup/cancel).
  const [panning, setPanning] = useState(false);

  // Pointer-device mode (persisted per device): 'mouse' → wheel zooms; 'touchpad' → two-finger scroll pans
  // (⌘/Ctrl+scroll and trackpad-pinch always zoom regardless). The ref feeds the native wheel listener.
  const [inputMode, setInputModeState] = useState<'mouse' | 'touchpad'>(() => {
    try {
      return localStorage.getItem('wb:inputMode') === 'mouse' ? 'mouse' : 'touchpad';
    } catch {
      return 'touchpad';
    }
  });
  const inputModeRef = useRef(inputMode);
  const setInputMode = useCallback((m: 'mouse' | 'touchpad') => {
    inputModeRef.current = m;
    setInputModeState(m);
    try {
      localStorage.setItem('wb:inputMode', m);
    } catch {
      /* ignore */
    }
  }, []);

  // Transient bottom-centre toast (e.g. "Link copied").
  const [flashMsg, setFlashMsg] = useState<string | null>(null);
  const flashTimerRef = useRef<number | null>(null);
  const flash = useCallback((msg: string) => {
    setFlashMsg(msg);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlashMsg(null), 1800);
  }, []);

  // Draft/commit metadata for the current pen mode — a highlighter is drawn wider and marked so the Pixi
  // layer renders it as a single translucent outline (uniform alpha, no per-overlap darkening).
  const penMeta = useCallback(
    () => ({ color: penModeRef.current === 'highlighter' ? markerColorRef.current : penColorRef.current, width: penModeRef.current === 'highlighter' ? penSizeRef.current * HIGHLIGHTER_SCALE : penSizeRef.current, highlighter: penModeRef.current === 'highlighter' }),
    [],
  );

  const [selectedIds, setSelectedState] = useState<string[]>([]);
  const selectedRef = useRef(selectedIds);
  const setSelected = useCallback((v: string[] | ((s: string[]) => string[])) => {
    const next = typeof v === 'function' ? (v as (s: string[]) => string[])(selectedRef.current) : v;
    selectedRef.current = next;
    setSelectedState(next);
  }, []);

  const [editingId, setEditingId] = useState<string | null>(null);
  // Comments: which thread's popover is open, and a not-yet-submitted draft pin (comment tool → click).
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [draftPin, setDraftPin] = useState<{ x: number; y: number } | null>(null);
  const activeCommentRef = useRef<string | null>(null);
  activeCommentRef.current = activeCommentId;
  const draftPinRef = useRef<{ x: number; y: number } | null>(null);
  draftPinRef.current = draftPin;
  const [draft, setDraft] = useState<Draft>(null);
  const [marquee, setMarquee] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  // The element under the pointer (select mode, idle) — shows its connection nubs; and the element a
  // connector-in-progress would attach to (drop-target highlight).
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [connectTargetId, setConnectTargetId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuEntry[] } | null>(null);

  const spaceRef = useRef(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const worldDomRef = useRef<HTMLDivElement>(null); // the world-transform div (driven imperatively during gestures)
  const pixiRef = useRef<PixiInkHandle>(null);
  const guidesRef = useRef<SnapGuidesHandle>(null);
  const selWrapRef = useRef<HTMLDivElement>(null); // wraps SelectionLayer so it can be translated imperatively during a drag
  const activeMoveIdsRef = useRef<Set<string> | null>(null); // ids being dragged imperatively (skip realtime overwrite)
  const camCommitRef = useRef<number | null>(null); // rAF id for the once-per-frame React `cam` commit
  const gesture = useRef<Gesture>({ kind: 'idle' });
  // Multi-touch pinch-zoom (mobile Safari) + eased-zoom animation.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  // Whether ⌘/Ctrl is currently held — tracked from the keyboard because iPad Safari does NOT set
  // metaKey/ctrlKey on wheel events, so ⌘+trackpad-scroll would otherwise fall through to pan.
  const zoomModRef = useRef(false);
  const pinchRef = useRef<{ startDist: number; startMid: { x: number; y: number }; startCam: Camera } | null>(null);
  // The pointer that owns the active gesture — window move/up ignore every other pointer, so a resting
  // palm (a second `touch`) can't inject points into a pen stroke or cancel it mid-draw.
  const activePointerRef = useRef<number | null>(null);
  const penRafRef = useRef<number | null>(null); // rAF-throttle the pen preview so getStroke isn't re-run per coalesced batch
  // Coalesce gesture pointermoves to ONE process per animation frame. A high-report-rate mouse (common on
  // Windows, up to 1000Hz) otherwise fires many pointermoves per frame, each doing a full setElements →
  // re-render → GPU redraw, saturating the main thread (the "10fps drag"). Pan/pen bypass this.
  const moveRafRef = useRef<number | null>(null);
  const lastMoveEventRef = useRef<PointerEvent | null>(null);
  // Edge auto-pan: a self-scheduling rAF loop that pans the camera while the cursor sits in the edge zone,
  // re-processing the LAST drag event each frame (kept here, distinct from the coalesce ref) so the dragged
  // element/connector keeps following the cursor as the world scrolls under it.
  const edgePanRafRef = useRef<number | null>(null);
  const dragEventRef = useRef<PointerEvent | null>(null);
  const rafRef = useRef<number | null>(null); // button/keyboard/fit tween
  // Wheel-zoom easing (trackpad pinch + ⌘/ctrl+scroll): accumulate a target zoom and ease toward it.
  const zoomTargetRef = useRef<number | null>(null);
  const zoomAnchorRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const zoomRafRef = useRef<number | null>(null);

  const els = useElements(whiteboardId, selfId);
  const opsRef = useRef(els);
  opsRef.current = els;

  // Board-item cards (SBB-13): the live store of items referenced by this whiteboard's item-cards.
  const itemStore = useCanvasItemsStore(els.elements);
  const itemStoreRef = useRef(itemStore);
  itemStoreRef.current = itemStore;
  // Item/search card pickers double as "add new" (retargetId undefined) and "change this card" (retargetId set).
  const [itemPicker, setItemPicker] = useState<{ retargetId?: string } | null>(null);
  const [searchPicker, setSearchPicker] = useState<{ retargetId?: string; query?: string } | null>(null);
  // Drop a new item-card at the viewport centre, referencing the chosen backlog item.
  const placeItemCard = (itemId: string, boardId: string) => {
    const c = screenToWorld(camRef.current, vpSize.w / 2, vpSize.h / 2);
    const d = DEFAULTS.item;
    els.create({ kind: 'item', x: c.x - d.w / 2, y: c.y - d.h / 2, w: d.w, h: d.h, style: { item_id: itemId, board_id: boardId } });
  };
  // Point an existing item-card at a different backlog item (keeps its position/size).
  const retargetItemCard = (id: string, itemId: string, boardId: string) => {
    const el = opsRef.current.byId(id);
    if (!el) return;
    opsRef.current.updateProps(id, { style: { ...el.style, item_id: itemId, board_id: boardId } }, { record: true });
  };
  // Drop a live search-results table at the viewport centre for a SparkQL query.
  const placeSearchCard = (query: string) => {
    const c = screenToWorld(camRef.current, vpSize.w / 2, vpSize.h / 2);
    const d = DEFAULTS.search;
    els.create({ kind: 'search', x: c.x - d.w / 2, y: c.y - d.h / 2, w: d.w, h: d.h, style: { query } });
  };
  // Change an existing search-card's query (clears cached board_ids so stale fan-out coverage drops; the
  // card re-runs and re-derives the boards it now spans).
  const retargetSearchCard = (id: string, query: string) => {
    const el = opsRef.current.byId(id);
    if (!el) return;
    if ((el.style?.query ?? '') === query) return; // unchanged → don't clear board_ids (would drop fan-out until the next poll)
    opsRef.current.updateProps(id, { style: { ...el.style, query, board_ids: [] } }, { record: true });
  };
  // A search-card reports the boards its results span → cache them on the element for the fan-out.
  // Guard against no-op writes at the WRITER (reads fresh element state): a search-card's poll can call
  // this from a stale closure with the same set it already stored — without this, that would re-persist
  // and re-broadcast board_ids to every peer every poll tick.
  const setSearchBoards = useCallback((id: string, boardIds: string[]) => {
    const el = opsRef.current.byId(id);
    if (!el) return;
    const cur = el.style?.board_ids ?? [];
    if (cur.length === boardIds.length && cur.every((b) => boardIds.includes(b))) return;
    opsRef.current.updateProps(id, { style: { ...el.style, board_ids: boardIds } }, { record: false });
  }, []);

  // Miro-like pinned comment threads (their own optimistic + realtime-reconciled store).
  const cm = useComments(whiteboardId, selfId, selfName);
  const cmRef = useRef(cm);
  cmRef.current = cm;

  // Undo an in-flight IMPERATIVE move (Escape / pinch-cancel): clear the Pixi drag offset + selection
  // wrapper and force one reconcile so every dragged ElementView rewrites its inline transform back to the
  // (unchanged) pre-move position.
  const abortImperativeMove = useCallback(() => {
    const g = gesture.current;
    if (edgePanRafRef.current != null) {
      cancelAnimationFrame(edgePanRafRef.current);
      edgePanRafRef.current = null;
    }
    dragEventRef.current = null;
    // Reset the dragged nodes to their pre-move transform IMPERATIVELY: a memoized ElementView won't
    // re-render from an identity-preserving setElements(slice) (unchanged x/y/selected), so relying on the
    // reconcile would leave the nodes stuck at the drag offset.
    if (g.kind === 'move' && g.nodes) {
      g.nodes.forEach((n, id) => {
        const o = g.originals[id];
        if (o) n.style.transform = `translate(${o.x}px, ${o.y}px)${o.rotation ? ` rotate(${o.rotation}deg)` : ''}`;
        n.style.willChange = '';
      });
    }
    pixiRef.current?.setDragOffset(null, 0, 0); // clear dragRef so the sync below is not skipped
    if (selWrapRef.current) selWrapRef.current.style.transform = '';
    activeMoveIdsRef.current = null;
    opsRef.current.setElements(opsRef.current.ref.current.slice()); // re-sync Pixi at pre-move coords (also resets ink offsets)
  }, []);

  // ---- realtime (own socket to this whiteboard's room) ----
  const rt = useWhiteboardRealtime(whiteboardId, {
    self: { id: selfId, name: selfName, color: selfColor },
    onUpsert: (el) => {
      if (activeMoveIdsRef.current?.has(el.id)) return; // don't let a peer echo overwrite a live imperative drag
      opsRef.current.mergeUpsert(el);
    },
    onDelete: (ids) => opsRef.current.mergeDelete(ids),
    onCommentUpsert: (c) => cmRef.current.mergeUpsert(c),
    onCommentDelete: (ids) => cmRef.current.mergeDelete(ids),
    // Item mutations fanned in from any board room (kanban/table/drawer/other whiteboard) → keep cards live.
    onItemUpsert: (it) => itemStoreRef.current.applyItemUpsert(it),
    onItemDelete: (ids) => itemStoreRef.current.applyItemDelete(ids),
    // A peer clicked "bring here" for us (or everyone) → jump to and start following their viewport.
    onBrought: (from, cam) => {
      flash(`${from.name || 'Someone'} brought you to their view`);
      followApiRef.current?.start(from.memberId, cam);
    },
    onReconnect: () => {
      opsRef.current.load();
      cmRef.current.load();
      itemStoreRef.current.reload();
    },
  });
  const sendCursorRef = useRef(rt.sendCursor);
  sendCursorRef.current = rt.sendCursor;
  const sendCameraRef = useRef(rt.sendCamera);
  sendCameraRef.current = rt.sendCamera;
  const setCameraSuppressedRef = useRef(rt.setCameraSuppressed);
  setCameraSuppressedRef.current = rt.setCameraSuppressed;
  // Follow / bring-to-me: refs read inside driveCam (defined below) so a user camera move breaks follow
  // and re-broadcasts our own viewport, while the follow loop drives without either side-effect.
  const followIdRef = useRef<string | null>(null);
  const stopFollowRef = useRef<() => void>(() => {});
  // A peer's "bring here" arrives before the follow machinery is defined → route through a ref.
  const followApiRef = useRef<{ start: (memberId: string, seed?: Camera) => void } | null>(null);
  // Elements a PEER has selected/locked (id → who): shaded + non-interactive locally. Ref for the
  // pointer-down guard so a locked element can't be grabbed.
  const locks = rt.locks;
  const locksRef = useRef(locks);
  locksRef.current = locks;
  // Broadcast what WE have selected / are editing so peers lock it (release when we deselect/stop editing).
  const sendSelection = rt.sendSelection;
  useEffect(() => {
    sendSelection(selectedIds, editingId);
  }, [selectedIds, editingId, sendSelection]);

  // ---- initial load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [board] = await Promise.all([api.getWhiteboard(whiteboardId), opsRef.current.load(), cmRef.current.load()]);
        if (cancelled) return;
        setWb(board);
      } catch {
        if (!cancelled) navigate('/whiteboards', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteboardId]);

  // Restore the initial view once, after the first load: a shared ?x&y&z link wins, else the view saved
  // for this board on this device, else fit-to-content.
  const didFit = useRef(false);
  useEffect(() => {
    if (didFit.current || loading) return;
    didFit.current = true;
    const r = viewportRef.current?.getBoundingClientRect();
    const vp = { w: r?.width ?? window.innerWidth, h: r?.height ?? window.innerHeight };
    let restore = camFromViewParams(window.location.search, vp);
    if (restore) {
      // Consume ONLY the view params, preserving any other query params + the hash (deep-links/anchors).
      const u = new URL(window.location.href);
      ['x', 'y', 'z'].forEach((k) => u.searchParams.delete(k));
      window.history.replaceState(null, '', u.pathname + u.search + u.hash);
    }
    if (!restore) {
      try {
        const s = localStorage.getItem(camKey(whiteboardId));
        if (s) {
          const c = JSON.parse(s) as Camera;
          if (Number.isFinite(c?.zoom) && c.zoom > 0) restore = c;
        }
      } catch {
        /* ignore */
      }
    }
    if (restore) {
      applyCam(restore);
      setCam(restore);
    } else {
      fitToContent(opsRef.current.ref.current, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Persist the view for this board (debounced) so a refresh reopens where you left off.
  useEffect(() => {
    if (loading || !whiteboardId) return undefined;
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(camKey(whiteboardId), JSON.stringify(cam));
      } catch {
        /* quota / private mode — non-fatal */
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [cam, whiteboardId, loading]);

  // ---- coordinate helpers ----
  const screenFromEvent = useCallback((e: { clientX: number; clientY: number }) => {
    const r = viewportRef.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  }, []);
  const worldFromEvent = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const s = screenFromEvent(e);
      return screenToWorld(camRef.current, s.x, s.y);
    },
    [screenFromEvent],
  );

  // ---- imperative camera ----
  // Write the camera straight to the DOM world-transform, the grid, and the GPU stage — no React
  // re-render. Gestures call this every frame (120fps) and only commit to React state throttled +
  // at the end, so overlays/virtualization stay correct without costing a render per frame.
  const applyCam = useCallback((c: Camera) => {
    const w = worldDomRef.current;
    if (w) w.style.transform = `translate(${c.x}px, ${c.y}px) scale(${c.zoom})`;
    const vp = viewportRef.current;
    if (vp) {
      let step = GRID * c.zoom;
      while (step < 22) step *= 2;
      while (step >= 44) step /= 2;
      vp.style.backgroundSize = `${step}px ${step}px`;
      vp.style.backgroundPosition = `${c.x}px ${c.y}px`;
    }
    pixiRef.current?.setCamera(c);
  }, []);
  // Commit the camera to React once per animation frame, so screen-space overlays (handles, toolbars,
  // cursors) re-render in lock-step with the imperative content instead of lagging behind a time throttle.
  const commitCamSoon = useCallback(() => {
    if (camCommitRef.current != null) return;
    camCommitRef.current = requestAnimationFrame(() => {
      camCommitRef.current = null;
      setCam(camRef.current);
    });
  }, [setCam]);
  /** Drive the camera: apply imperatively every call; commit to React now (`commit`) or next frame.
   *  `fromFollow` marks a frame coming from the follow loop — such frames neither break follow nor
   *  re-broadcast; any OTHER (user-driven) frame breaks an active follow and broadcasts our viewport. */
  const driveCam = useCallback(
    (next: Camera, commit = false, fromFollow = false) => {
      if (!fromFollow) {
        if (followIdRef.current) stopFollowRef.current();
        sendCameraRef.current(next); // broadcast our viewport for anyone following us
      }
      camRef.current = next;
      applyCam(next);
      if (commit) {
        if (camCommitRef.current != null) {
          cancelAnimationFrame(camCommitRef.current);
          camCommitRef.current = null;
        }
        setCam(next);
      } else {
        commitCamSoon();
      }
    },
    [applyCam, commitCamSoon, setCam],
  );
  // Keep the DOM/grid/GPU in sync whenever React `cam` changes by a non-gesture path (fit, restore, etc.).
  useEffect(() => {
    applyCam(camRef.current);
  }, [cam, applyCam]);

  const cancelTween = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);
  const cancelZoomEase = useCallback(() => {
    if (zoomRafRef.current != null) {
      cancelAnimationFrame(zoomRafRef.current);
      zoomRafRef.current = null;
    }
    zoomTargetRef.current = null;
  }, []);
  // Ease the camera zoom toward the accumulating `zoomTargetRef`, anchored at the cursor. Each wheel
  // event nudges the target (proportional to its delta); this loop glides the actual zoom toward it,
  // so trackpad pinch / ⌘+scroll feel smooth and track the gesture instead of snapping per event.
  const startZoomEase = useCallback(() => {
    if (zoomRafRef.current != null) return;
    const step = () => {
      const target = zoomTargetRef.current;
      const c = camRef.current;
      if (target == null) {
        zoomRafRef.current = null;
        return;
      }
      const next = c.zoom + (target - c.zoom) * ZOOM_EASE;
      const done = Math.abs(target - next) < 0.0008;
      const zoom = done ? target : next;
      const a = zoomAnchorRef.current;
      const k = zoom / c.zoom;
      driveCam({ zoom, x: a.x - (a.x - c.x) * k, y: a.y - (a.y - c.y) * k }, done);
      if (done) {
        zoomTargetRef.current = null;
        zoomRafRef.current = null;
      } else {
        zoomRafRef.current = requestAnimationFrame(step);
      }
    };
    zoomRafRef.current = requestAnimationFrame(step);
  }, [driveCam]);
  // Ease the camera to a target (ease-out cubic) — the smooth zoom Miro uses for buttons/keys/fit.
  const tweenCam = useCallback(
    (target: Camera, ms = 200) => {
      cancelTween();
      cancelZoomEase();
      const start = { ...camRef.current };
      const t0 = performance.now();
      const ease = (t: number) => 1 - Math.pow(1 - t, 3);
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / ms);
        const e = ease(t);
        driveCam({ x: start.x + (target.x - start.x) * e, y: start.y + (target.y - start.y) * e, zoom: start.zoom + (target.zoom - start.zoom) * e }, t >= 1);
        rafRef.current = t < 1 ? requestAnimationFrame(step) : null;
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [cancelTween, cancelZoomEase, driveCam],
  );
  const zoomButton = useCallback(
    (factor: number) => {
      const vp = viewportRef.current;
      const cx = (vp?.clientWidth ?? 0) / 2;
      const cy = (vp?.clientHeight ?? 0) / 2;
      const c = camRef.current;
      const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, c.zoom * factor));
      const k = zoom / c.zoom;
      tweenCam({ zoom, x: cx - (cx - c.x) * k, y: cy - (cy - c.y) * k }, 180);
    },
    [tweenCam],
  );

  const fitToContent = useCallback(
    (all: CanvasElement[], animate = true) => {
      const vp = viewportRef.current;
      if (!vp) return;
      const W = vp.clientWidth;
      const H = vp.clientHeight;
      const b = boundsOf(all);
      const target: Camera = !b || b.w === 0 || b.h === 0 ? { x: W / 2, y: H / 2, zoom: 1 } : (() => {
        const pad = 80;
        const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.min((W - pad * 2) / b.w, (H - pad * 2) / b.h, 1.5)));
        return { zoom, x: W / 2 - (b.x + b.w / 2) * zoom, y: H / 2 - (b.y + b.h / 2) * zoom };
      })();
      if (animate) tweenCam(target, 280);
      else driveCam(target, true);
    },
    [driveCam, tweenCam],
  );

  // Preview thumbnails are rendered SERVER-SIDE (debounced after edits, incl. MCP/programmatic ones) —
  // see backend whiteboard_snapshot.go. The client neither captures nor uploads them.

  // ---- follow / bring-to-me (walk through together) ----
  // Follow a peer: ease OUR camera toward theirs every frame (position + zoom), so it glides smoothly and
  // tracks their pan/zoom — NOT their mouse cursor (that would be nauseating). Any user camera input
  // breaks it (via driveCam). `followId` is the peer member id; `followSeedRef` is the initial target
  // from a "bring" before their camera stream has arrived.
  const [followId, setFollowIdState] = useState<string | null>(null);
  const followRafRef = useRef<number | null>(null);
  const followSeedRef = useRef<Camera | null>(null);
  const getPeerCameraRef = useRef(rt.getPeerCamera);
  getPeerCameraRef.current = rt.getPeerCamera;

  const stopFollow = useCallback(() => {
    if (!followIdRef.current) return;
    followIdRef.current = null;
    followSeedRef.current = null;
    if (followRafRef.current != null) {
      cancelAnimationFrame(followRafRef.current);
      followRafRef.current = null;
    }
    setCameraSuppressedRef.current(false); // resume our own camera heartbeat…
    sendCameraRef.current(camRef.current); // …seeded with where we actually ended up (the presenter's view)
    setFollowIdState(null);
  }, []);
  stopFollowRef.current = stopFollow;

  const startFollow = useCallback(
    (memberId: string, seed?: Camera) => {
      if (!memberId || memberId === selfId) return;
      cancelTween(); // supersede any in-flight tween/zoom-ease, else its next (non-follow) frame self-breaks the follow
      cancelZoomEase();
      setCameraSuppressedRef.current(true); // we're a passenger now — stop heartbeating our (stale) camera
      followIdRef.current = memberId;
      followSeedRef.current = seed ?? getPeerCameraRef.current(memberId) ?? camRef.current;
      setFollowIdState(memberId);
      if (followRafRef.current != null) return; // loop already running (re-target only)
      const step = () => {
        const fid = followIdRef.current;
        if (!fid) {
          followRafRef.current = null;
          return;
        }
        const peer = getPeerCameraRef.current(fid);
        const target: Camera | null = peer ? { x: peer.x, y: peer.y, zoom: peer.zoom } : followSeedRef.current;
        if (target) {
          if (peer) followSeedRef.current = null; // their live stream has taken over from the bring seed
          const c = camRef.current;
          const dx = Math.abs(target.x - c.x);
          const dy = Math.abs(target.y - c.y);
          const dz = Math.abs(target.zoom - c.zoom);
          // Idle/converged: once caught up, snap exactly once then STOP driving React each frame — a still
          // presenter must not re-render the whole page at 60fps. We resume easing when their camera moves.
          if (dx < 0.4 && dy < 0.4 && dz < 0.0006) {
            if (c.x !== target.x || c.y !== target.y || c.zoom !== target.zoom) driveCam(target, true, true);
          } else {
            driveCam({ x: c.x + (target.x - c.x) * FOLLOW_EASE, y: c.y + (target.y - c.y) * FOLLOW_EASE, zoom: c.zoom + (target.zoom - c.zoom) * FOLLOW_EASE }, false, true);
          }
        }
        followRafRef.current = requestAnimationFrame(step);
      };
      followRafRef.current = requestAnimationFrame(step);
    },
    [driveCam, cancelTween, cancelZoomEase, selfId],
  );
  followApiRef.current = { start: startFollow };

  // Stop following someone who has left the whiteboard (they're gone from presence).
  useEffect(() => {
    if (followId && !rt.presence.includes(followId)) stopFollow();
  }, [followId, rt.presence, stopFollow]);

  // Tear down the follow loop (and any pending camera-commit rAF) on unmount — refs aren't auto-cleared,
  // so navigating away WHILE following would otherwise leave a 60fps rAF spinning and pin this closure.
  useEffect(
    () => () => {
      stopFollowRef.current();
      if (camCommitRef.current != null) {
        cancelAnimationFrame(camCommitRef.current);
        camCommitRef.current = null;
      }
    },
    [],
  );

  // "Bring here": push a peer (or everyone) into following US, seeded with our current viewport.
  const bringToMe = useCallback(
    (target: string) => rt.sendBring(target, camRef.current),
    [rt],
  );

  // Seed our camera on (re)connect so an idle presenter is immediately followable (the heartbeat then
  // keeps it fresh); without this, we'd be invisible to followers until our first pan/zoom.
  useEffect(() => {
    if (rt.connected) sendCameraRef.current(camRef.current);
  }, [rt.connected]);

  // ---- track ⌘/Ctrl held state from the keyboard (see zoomModRef) ----
  useEffect(() => {
    const sync = (e: KeyboardEvent) => {
      zoomModRef.current = e.metaKey || e.ctrlKey;
    };
    const clear = () => {
      zoomModRef.current = false;
    };
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
      window.removeEventListener('blur', clear);
    };
  }, []);

  // ---- native wheel (non-passive so we can preventDefault) ----
  // Depends on `loading` so it (re)binds once the real viewport div mounts: while the loading spinner
  // is shown the ref is null, and without this dep the listener would never attach → no scroll-to-pan.
  useEffect(() => {
    if (loading) return undefined;
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const onWheel = (e: WheelEvent) => {
      // A wheel over a floating overlay (comment popover, inspector, toolbar, top bar, flyout) scrolls
      // THAT panel natively — don't preventDefault or pan/zoom the canvas beneath it.
      if ((e.target as HTMLElement | null)?.closest?.('[data-wb-overlay]')) return;
      e.preventDefault();
      cancelTween(); // stop any button/keyboard zoom animation
      if (e.ctrlKey || e.metaKey || zoomModRef.current || inputModeRef.current === 'mouse') {
        // Zoom when: trackpad pinch (macOS reports ctrlKey) / ⌘/ctrl+scroll / iPad ⌘ (zoomModRef) — OR the
        // device is set to MOUSE mode (a wheel has no pan gesture, so scroll = zoom). The factor is
        // PROPORTIONAL to the scroll delta and EASES toward an accumulating target anchored at the pointer.
        const mode = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? vp.clientHeight : 1;
        const dy = Math.max(-40, Math.min(40, e.deltaY * mode)); // clamp so no single event jumps far
        // A trackpad PINCH reports ctrlKey (synthetic) — give it (and a real ⌃+scroll zoom) the extra
        // pinch gain; ⌘+scroll and mouse-mode scroll-zoom keep the base sensitivity.
        const sensitivity = e.ctrlKey ? ZOOM_WHEEL_SENSITIVITY * PINCH_ZOOM_GAIN : ZOOM_WHEEL_SENSITIVITY;
        const factor = Math.exp(-dy * sensitivity);
        const cur = zoomTargetRef.current ?? camRef.current.zoom;
        zoomTargetRef.current = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, cur * factor));
        zoomAnchorRef.current = screenFromEvent(e);
        startZoomEase();
      } else {
        // Two-finger scroll / mouse wheel → pan. Normalise line/page delta modes to pixels.
        cancelZoomEase();
        const f = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? vp.clientHeight : 1;
        const c = camRef.current;
        driveCam({ ...c, x: c.x - e.deltaX * f, y: c.y - e.deltaY * f });
        if (wheelIdle) clearTimeout(wheelIdle);
        wheelIdle = setTimeout(() => setCam(camRef.current), 120); // trailing React sync (wheel has no "end")
      }
    };
    let wheelIdle: ReturnType<typeof setTimeout> | null = null;
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      vp.removeEventListener('wheel', onWheel);
      if (wheelIdle) clearTimeout(wheelIdle);
    };
  }, [loading, screenFromEvent, driveCam, setCam, cancelTween, cancelZoomEase, startZoomEase]);

  // ---- Safari pinch GESTURE events. On iPad, a trackpad (Magic Keyboard) pinch is NOT ctrl+wheel — it
  // fires Safari's non-standard gesture{start,change,end}, and if we don't preventDefault it zooms the
  // whole PAGE instead of the canvas. Intercept them and drive the canvas zoom. A touchscreen pinch also
  // fires these, but the pointer-pinch below already owns that (two touch pointers), so we defer to it
  // whenever any touch pointer is down and only act when there are none (i.e. a trackpad/mouse gesture). ----
  useEffect(() => {
    if (loading) return undefined;
    const vp = viewportRef.current;
    if (!vp) return undefined;
    let g: { zoom: number; x: number; y: number; ax: number; ay: number } | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const start = (e: any) => {
      e.preventDefault(); // stop Safari's native page zoom
      if (pointersRef.current.size > 0) {
        g = null;
        return; // a finger is down → this is a touchscreen pinch, handled by the pointer-pinch
      }
      cancelTween();
      cancelZoomEase();
      const s = screenFromEvent(e);
      g = { zoom: camRef.current.zoom, x: camRef.current.x, y: camRef.current.y, ax: s.x, ay: s.y };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const change = (e: any) => {
      e.preventDefault();
      if (!g) return;
      // e.scale is cumulative from gesturestart (1 → …). Anchor the zoom at the gesture's start point.
      const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, g.zoom * Math.pow(e.scale, PINCH_ZOOM_GAIN)));
      const k = zoom / g.zoom;
      driveCam({ zoom, x: g.ax - (g.ax - g.x) * k, y: g.ay - (g.ay - g.y) * k });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const end = (e: any) => {
      e.preventDefault();
      if (!g) return;
      g = null;
      setCam(camRef.current); // final React sync
    };
    vp.addEventListener('gesturestart', start, { passive: false });
    vp.addEventListener('gesturechange', change, { passive: false });
    vp.addEventListener('gestureend', end, { passive: false });
    return () => {
      vp.removeEventListener('gesturestart', start);
      vp.removeEventListener('gesturechange', change);
      vp.removeEventListener('gestureend', end);
    };
  }, [loading, screenFromEvent, driveCam, setCam, cancelTween, cancelZoomEase]);

  // ---- multi-touch pinch-zoom (mobile Safari / iPad). Two fingers zoom+pan around their midpoint. ----
  useEffect(() => {
    if (loading) return undefined;
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const pointers = pointersRef.current;
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return; // trackpad pinch arrives as wheel+ctrlKey
      if (gesture.current.kind === 'pen') return; // don't let a resting palm hijack an active pen stroke into a pinch
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [p1, p2] = [...pointers.values()];
        pinchRef.current = { startDist: Math.hypot(p2.x - p1.x, p2.y - p1.y), startMid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }, startCam: { ...camRef.current } };
        if (['move', 'resize', 'groupResize', 'rotate'].includes(gesture.current.kind)) abortImperativeMove(); // reset transforms + clear the Pixi-skip
        gesture.current = { kind: 'idle' }; // cancel the single-finger gesture that was in progress
        setDraft(null);
        guidesRef.current?.clear(); // a pinch cancels an in-progress drag → drop its alignment guides
        setMarquee(null);
        cancelTween();
        cancelZoomEase();
      }
    };
    const move = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pr = pinchRef.current;
      if (pointers.size === 2 && pr) {
        const [p1, p2] = [...pointers.values()];
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const r = vp.getBoundingClientRect();
        const mx = (p1.x + p2.x) / 2 - r.left;
        const my = (p1.y + p2.y) / 2 - r.top;
        const smx = pr.startMid.x - r.left;
        const smy = pr.startMid.y - r.top;
        const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pr.startCam.zoom * Math.pow(dist / pr.startDist, PINCH_ZOOM_GAIN)));
        const k = zoom / pr.startCam.zoom;
        driveCam({ zoom, x: mx - (smx - pr.startCam.x) * k, y: my - (smy - pr.startCam.y) * k });
      }
    };
    const up = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2 && pinchRef.current) {
        pinchRef.current = null;
        setCam(camRef.current); // final React sync when the pinch ends
      }
    };
    window.addEventListener('pointerdown', down, { passive: true });
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerup', up, { passive: true });
    window.addEventListener('pointercancel', up, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      pointers.clear();
      pinchRef.current = null;
    };
  }, [loading, driveCam, setCam, cancelTween, cancelZoomEase]);

  // iPad/Safari swallows the *second* of two rapid taps (pen OR finger) — no pointer/touch/gesture event
  // fires at all — because it enters "double-tap-to-zoom" detection on the first tap and suppresses the
  // rest of the gesture. `touch-action: none` does NOT stop this for the Pencil; calling preventDefault()
  // on the FIRST tap's touchstart does — it tells Safari the page owns the touch, so it never starts
  // double-tap detection and every subsequent tap is delivered normally. Scoped so real controls (buttons,
  // inputs, the text editor) and element double-tap-to-edit (.group) keep their native behaviour.
  useEffect(() => {
    if (loading) return;
    const onTouchStart = (e: TouchEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || t.closest?.('button, input, textarea, select, a, [contenteditable], [data-toolbar], [data-native-touch]')) return;
      // Pen tool: never let Safari run a touch gesture. Other tools: suppress on the empty canvas but leave
      // elements (.group) alone so double-tapping a sticky still opens its editor.
      if (toolRef.current === 'pen' || !t.closest?.('.group')) e.preventDefault();
    };
    document.addEventListener('touchstart', onTouchStart, { passive: false });
    return () => document.removeEventListener('touchstart', onTouchStart);
  }, [loading]);

  // ---- selection helpers ----
  const selectForDrag = useCallback(
    (id: string, shift: boolean) => {
      if (shift) {
        setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
      } else if (!selectedRef.current.includes(id)) {
        setSelected([id]);
      }
    },
    [setSelected],
  );

  const startMove = useCallback(
    (e: ReactPointerEvent) => {
      setInteracting(true);
      // Alt/Opt + drag = duplicate: clone the current selection IN PLACE, then drag the CLONES so the
      // originals stay put (the Figma/Miro convention). We drive the move from the freshly-created
      // copies directly — `byId` can't find them yet (the elements ref only syncs on the next render,
      // so a lookup here returns undefined and nothing would drag).
      if (e.altKey) {
        const copies: string[] = [];
        const dupOrigins: Record<string, { x: number; y: number; rotation: number }> = {};
        // flushSync so the clones + their selection COMMIT before we return — otherwise the first move rAF
        // can run before React mounts the clone DOM nodes, the node-map captures nothing, and a rapid
        // back-to-back Alt-drag "does nothing" (the copy lands stacked on its source).
        flushSync(() => {
          selectedRef.current.forEach((id) => {
            const el = opsRef.current.byId(id);
            if (!el || el.style?.locked) return;
            const copy = opsRef.current.create({
              kind: el.kind,
              x: el.x,
              y: el.y,
              w: el.w,
              h: el.h,
              rotation: el.rotation,
              color: el.color,
              content: el.content,
              from_id: el.from_id,
              to_id: el.to_id,
              style: el.style,
            });
            copies.push(copy.id);
            dupOrigins[copy.id] = { x: el.x, y: el.y, rotation: el.rotation ?? 0 };
          });
          if (copies.length) setSelected(copies);
        });
        if (copies.length) {
          activeMoveIdsRef.current = new Set(copies);
          gesture.current = { kind: 'move', startWorld: worldFromEvent(e), originals: dupOrigins, dx: 0, dy: 0, moved: false };
          return;
        }
      }
      const originals: Record<string, { x: number; y: number; rotation: number }> = {};
      selectedRef.current.forEach((id) => {
        const el = opsRef.current.byId(id);
        // Connectors are derived (no independent position → moving them writes phantom geometry); locked
        // elements are pinned. Exclude both so a group drag touches only real, movable members.
        if (el && el.kind !== 'connector' && !el.style?.locked) originals[id] = { x: el.x, y: el.y, rotation: el.rotation ?? 0 };
      });
      activeMoveIdsRef.current = new Set(Object.keys(originals));
      gesture.current = { kind: 'move', startWorld: worldFromEvent(e), originals, dx: 0, dy: 0, moved: false };
    },
    [worldFromEvent, setSelected],
  );

  /** Persist a finished pen stroke as a freehand element (a 1-point tap becomes a tiny dot). */
  const commitPen = useCallback((stroke: number[][]) => {
    dbg(`commitPen len=${stroke?.length ?? 0}`);
    if (!stroke || stroke.length < 1) return;
    const s = stroke.length === 1 ? [stroke[0], [stroke[0][0] + 0.01, stroke[0][1] + 0.01, stroke[0][2]]] : stroke;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of s) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const meta = penMeta();
    const pad = meta.width;
    minX -= pad;
    minY -= pad;
    maxX += pad;
    maxY += pad;
    const rel = s.map(([x, y, p]) => [x - minX, y - minY, p]);
    const el = opsRef.current.create({ kind: 'freehand', x: minX, y: minY, w: maxX - minX, h: maxY - minY, color: meta.color, style: { stroke: rel, strokeWidth: meta.width, ...(meta.highlighter ? { highlighter: true } : {}) } });
    dbg(`created ${el.id.slice(0, 4)} total=${opsRef.current.ref.current.filter((e) => e.kind === 'freehand').length}`);
  }, [penMeta]);

  const startCreateOrDraw = useCallback(
    (e: ReactPointerEvent) => {
      const t = toolRef.current;
      const world = worldFromEvent(e);
      if (t === 'pen') {
        dbg(`start #${e.pointerId} (was ${gesture.current.kind})`);
        // If a previous pen stroke is somehow still open (a missed/late pointerup under load), flush it
        // instead of dropping it — the cause of occasional "skipped" strokes when writing fast.
        if (gesture.current.kind === 'pen' && gesture.current.stroke.length) commitPen(gesture.current.stroke);
        const pr = clampPressure((e.pressure ?? 0.5) || 0.5);
        const pt: number[][] = [[world.x, world.y, pr]];
        gesture.current = { kind: 'pen', stroke: pt };
        setDraft({ kind: 'pen', stroke: pt.slice(), ...penMeta() });
      } else {
        gesture.current = { kind: 'create', startWorld: world, tool: t };
      }
    },
    [worldFromEvent, commitPen, penMeta],
  );

  // ---- element / handle / background pointer-down ----
  /** Begin drawing a connector from an element (nub, connector tool, or a border edge-drag). */
  const startConnectorFrom = useCallback(
    (e: ReactPointerEvent, id: string, fromEdge = false) => {
      if (e.button !== 0) return; // only the LEFT button draws/edits — let middle-drag bubble up to pan
      e.stopPropagation();
      setSelected([]);
      setHoveredId(null);
      const from = opsRef.current.byId(id);
      if (!from) return;
      gesture.current = { kind: 'connector', fromId: id, fromEdge, startScreen: screenFromEvent(e), startWorld: worldFromEvent(e) };
      setDraft({ kind: 'connector', from, to: worldFromEvent(e), color: '#868e96' });
    },
    [setSelected, worldFromEvent, screenFromEvent],
  );

  const onElementPointerDown = useCallback(
    (e: ReactPointerEvent, id: string) => {
      if (editingId) return;
      if (locksRef.current.has(id)) return; // a peer has this element locked — non-interactive here
      if (e.button === 2) return; // right-click → let the context menu handle it
      // A second pointer can't hijack an ACTIVE stroke (stroke integrity); it never blocks a fresh one.
      if (gesture.current.kind !== 'idle' && e.pointerType === 'touch' && e.pointerId !== activePointerRef.current) return;
      e.stopPropagation();
      setHoveredId(null);
      activePointerRef.current = e.pointerId;
      const t = toolRef.current;
      if (t === 'pan' || spaceRef.current || e.button === 1) {
        setPanning(true);
        gesture.current = { kind: 'pan', startScreen: screenFromEvent(e), startCam: { x: camRef.current.x, y: camRef.current.y } };
        return;
      }
      if (t === 'connector') {
        startConnectorFrom(e, id, false);
        return;
      }
      if (t !== 'select') {
        startCreateOrDraw(e);
        return;
      }
      const el = opsRef.current.byId(id);
      // Connectors start ONLY from the hover nubs (ConnectionNubs) or the connector tool — NOT from a
      // border-drag. Grabbing an element anywhere (border included) just selects/moves it, so panning or
      // stray drags near an edge never spawn an accidental connector.
      selectForDrag(id, e.shiftKey);
      if (el?.style?.locked) return; // locked: selectable (to unlock) but not movable
      startMove(e);
    },
    [editingId, screenFromEvent, selectForDrag, startMove, startCreateOrDraw, startConnectorFrom],
  );

  const onHandlePointerDown = useCallback(
    (e: ReactPointerEvent, handle: Handle) => {
      if (e.button !== 0) return; // only the LEFT button resizes/rotates — middle-drag bubbles up to pan
      e.stopPropagation();
      setHoveredId(null);
      setInteracting(true);
      // Multi-selection: a corner handle scales the whole group proportionally about the opposite corner.
      const sel = selectedRef.current;
      if (sel.length > 1) {
        // Locked members are pinned — they neither frame nor transform with the group.
        const active = (sel.map((id) => opsRef.current.byId(id)).filter(Boolean) as CanvasElement[]).filter((el) => !el.style?.locked);
        const frame = active.filter((el) => SNAP_KINDS.has(el.kind)); // real-geometry members define the box
        const b = boundsOf(frame);
        if (!b || b.w < 1 || b.h < 1) return;
        const originals: Record<string, { x: number; y: number; w: number; h: number; rotation: number; style: ElementStyle; kind: string }> = {};
        for (const el of active) originals[el.id] = { x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation, style: el.style ?? {}, kind: el.kind };
        gesture.current = { kind: 'groupResize', handle, bounds: b, originals, moved: false };
        return;
      }
      const el = opsRef.current.byId(sel[0]);
      if (!el) return;
      if (handle === 'rotate') {
        const center = elementCenter(el);
        gesture.current = { kind: 'rotate', id: el.id, center, startAngle: angleTo(center, worldFromEvent(e)), prevRotation: el.rotation };
      } else {
        // Corners scale proportionally (and grow the font); edges reflow width/height freely — for every
        // kind, stickies included (they used to be locked square).
        const corner = handle === 'nw' || handle === 'ne' || handle === 'se' || handle === 'sw';
        const mode: ResizeMode = corner ? 'aspect' : 'free';
        const scaleFont = corner && (el.kind === 'sticky' || el.kind === 'shape' || el.kind === 'text');
        // Edge (free) resize of a sticky shrinks the font so the text keeps fitting instead of clipping —
        // and grows it back up to the starting size when enlarged. Corners already scale the font (aspect).
        const fitFont = !corner && el.kind === 'sticky';
        const origFont = el.style?.fontSize ?? (el.kind === 'text' || el.kind === 'sticky' ? 24 : 16);
        // Item/search cards are content-height-driven: a resize only sets WIDTH (autoHeight owns h), so the
        // side handles just make the card wider to show more text without ever fighting the auto height.
        const widthOnly = el.kind === 'item' || el.kind === 'search';
        gesture.current = { kind: 'resize', id: el.id, handle, prev: geomOf(el), mode, scaleFont, fitFont, origStyle: el.style ?? {}, origFont, widthOnly };
      }
    },
    [worldFromEvent],
  );

  /** Begin a drag-to-place from a palette tool button (drop on the canvas → create there). */
  const onPlaceStart = useCallback(
    (t: Tool, e: ReactPointerEvent) => {
      gesture.current = { kind: 'place', tool: t, startScreen: screenFromEvent(e), moved: false };
    },
    [screenFromEvent],
  );

  /** Begin dragging an existing connector waypoint. */
  const onWaypointDown = useCallback((e: ReactPointerEvent, index: number) => {
    if (e.button !== 0) return; // left button only — middle-drag bubbles up to pan
    e.stopPropagation();
    const id = selectedRef.current[0];
    const el = opsRef.current.byId(id);
    if (!el) return;
    gesture.current = { kind: 'waypoint', id, index, origStyle: el.style ?? {} };
  }, []);

  /** Begin dragging a connector segment-midpoint dot (elbow → moves the segment; straight/curved → bends). */
  const onSegmentDown = useCallback(
    (e: ReactPointerEvent, controlIndex: number, orientation: 'h' | 'v' | 'free' | 'railh' | 'railv', mid: { x: number; y: number }) => {
      if (e.button !== 0) return; // left button only — middle-drag bubbles up to pan
      e.stopPropagation();
      const id = selectedRef.current[0];
      const el = opsRef.current.byId(id);
      if (!el) return;
      gesture.current = { kind: 'segment', id, controlIndex, orientation, mid, startScreen: screenFromEvent(e), origStyle: el.style ?? {}, added: false };
    },
    [screenFromEvent],
  );

  /** Begin dragging a connector's text label ALONG its path (updates style.labelT). */
  const onLabelDown = useCallback((e: ReactPointerEvent, id: string) => {
    if (e.button !== 0) return; // left button only — middle-drag bubbles up to pan
    e.stopPropagation();
    const el = opsRef.current.byId(id);
    if (!el) return;
    const from = el.from_id ? opsRef.current.byId(el.from_id) : undefined;
    const to = el.to_id ? opsRef.current.byId(el.to_id) : undefined;
    if (!from || !to) return;
    activePointerRef.current = e.pointerId;
    setSelected([id]);
    gesture.current = { kind: 'label', id, samplePts: connectorGeometry(from, to, el.style ?? {}).samplePts, origStyle: el.style ?? {}, moved: false };
  }, [setSelected]);

  /** Begin dragging a connector endpoint to re-attach it to another shape. */
  const onEndpointDown = useCallback((e: ReactPointerEvent, which: 'from' | 'to') => {
    if (e.button !== 0) return; // left button only — middle-drag bubbles up to pan
    e.stopPropagation();
    const id = selectedRef.current[0];
    const el = opsRef.current.byId(id);
    if (!el) return;
    gesture.current = { kind: 'endpoint', id, which, origFromId: el.from_id, origToId: el.to_id, origStyle: el.style ?? {} };
  }, []);

  const onBackgroundPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button === 2) return; // right-click → context menu, not a marquee/create
      // A second pointer can't hijack an ACTIVE gesture (e.g. a resting palm while inking) — but this
      // never blocks a fresh stroke, and we don't do any timing/size palm heuristics: iPadOS already
      // distinguishes the Pencil (pointerType 'pen') from touch, so we just trust the platform.
      if (gesture.current.kind !== 'idle' && e.pointerType === 'touch' && e.pointerId !== activePointerRef.current) return;
      if (editingId) setEditingId(null);
      setHoveredId(null);
      const t = toolRef.current;
      activePointerRef.current = e.pointerId;
      // Hand/space/middle pan; also FINGER (touch) always pans+pinches with the select or pen tool, so the
      // stylus is the only thing that draws (a 2nd finger promotes to pinch). Keeps touch out of the draw
      // path entirely — the platform's own Pencil/touch split does the palm rejection for us.
      // Comment tool: drop a draft pin where you click and open the composer (Miro-style). Placing the
      // thread (on submit) reverts to the select tool.
      if (t === 'comment') {
        const world = worldFromEvent(e);
        setDraftPin({ x: world.x, y: world.y });
        setActiveCommentId(null);
        return;
      }
      if (t === 'pan' || spaceRef.current || e.button === 1 || (e.pointerType === 'touch' && (t === 'select' || t === 'pen'))) {
        setPanning(true);
        gesture.current = { kind: 'pan', startScreen: screenFromEvent(e), startCam: { x: camRef.current.x, y: camRef.current.y } };
        return;
      }
      // A background click (select / create tools) closes an open comment thread or discards a draft pin.
      if (activeCommentRef.current) setActiveCommentId(null);
      if (draftPinRef.current) setDraftPin(null);
      if (t === 'select') {
        // Ink lives on the (pointer-transparent) GPU layer, so resolve a click on a stroke here.
        const world = worldFromEvent(e);
        const m = 6 / camRef.current.zoom;
        const inkHit = opsRef.current.ref.current
          .filter((el) => el.kind === 'freehand')
          .sort((a, b) => b.z_index - a.z_index)
          .find((el) => freehandHit(el, world, m));
        if (inkHit) {
          selectForDrag(inkHit.id, e.shiftKey);
          if (!inkHit.style?.locked) startMove(e);
          return;
        }
        if (!e.shiftKey) setSelected([]);
        gesture.current = { kind: 'marquee', startScreen: screenFromEvent(e), base: e.shiftKey ? selectedRef.current : [] };
        return;
      }
      if (t === 'connector') return;
      startCreateOrDraw(e);
    },
    [editingId, screenFromEvent, worldFromEvent, setSelected, selectForDrag, startMove, startCreateOrDraw],
  );

  const onViewportPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      // Over a floating overlay (popover/inspector/toolbar): don't hover-highlight or broadcast a cursor
      // for the canvas beneath it — the pointer is interacting with the panel, not the board.
      if ((e.target as HTMLElement).closest?.('[data-wb-overlay]')) {
        setHoveredId(null); // React bails when already null, so this is cheap
        return;
      }
      const world = worldFromEvent(e);
      sendCursorRef.current(world);
      // Track the hovered element (for its connection nubs) only while idle in the select tool.
      if (toolRef.current === 'select' && gesture.current.kind === 'idle' && !editingId) {
        const m = 24 / camRef.current.zoom; // world-space hover margin so edge nubs stay reachable
        const hit = [...opsRef.current.ref.current]
          .filter((el) => CONNECTABLE.has(el.kind))
          .sort((a, b) => b.z_index - a.z_index)
          .find((el) => hitTestMargin(el, world, m));
        setHoveredId(hit ? hit.id : null);
      }
    },
    [worldFromEvent, editingId],
  );

  /** Begin a connector drag FROM an element's nub (Miro on-object behaviour — no connector tool). */
  /** Create an element of a placeable tool at a rect, select it, and (sticky/text) enter edit mode. */
  const spawn = useCallback(
    (t: Tool, rect: { x: number; y: number; w: number; h: number }): void => {
      let el: CanvasElement | null = null;
      if (t === 'sticky') el = opsRef.current.create({ kind: 'sticky', ...rect, color: null, style: { fontSize: 24 } });
      else if (t === 'text') el = opsRef.current.create({ kind: 'text', ...rect, color: null, style: { fontSize: 24 } });
      else if (t === 'shape') el = opsRef.current.create({ kind: 'shape', ...rect, color: colorRef.current, style: { shape: shapeVariantRef.current } });
      if (el) {
        setSelected([el.id]);
        setTool('select');
        if (el.kind === 'sticky' || el.kind === 'text') setEditingId(el.id);
      }
    },
    [setSelected, setTool],
  );

  // ---- window pointer move / up (drive the active gesture) ----
  useEffect(() => {
    // Coalesce the pen preview into one setDraft per frame — getStroke over the whole stroke is O(n),
    // so re-running it per coalesced batch would be O(n²) and lag long strokes. Points are never dropped
    // (they're already in gesture.stroke); only the redraw is throttled.
    const schedulePenDraft = () => {
      if (penRafRef.current != null) return;
      penRafRef.current = requestAnimationFrame(() => {
        penRafRef.current = null;
        const g = gesture.current;
        if (g.kind === 'pen') setDraft({ kind: 'pen', stroke: g.stroke.slice(), ...penMeta() });
      });
    };
    // The actual per-gesture processing (may setElements → re-render). Called at most once per frame for
    // most gestures; pan runs it inline (imperative + cheap, wants immediacy); pen runs it inline (needs
    // every event's coalesced sub-points for stroke fidelity).
    const processMove = (e: PointerEvent) => {
      const g = gesture.current;
      if (g.kind === 'idle') return;
      const world = worldFromEvent(e);
      if (g.kind === 'pan') {
        const s = screenFromEvent(e);
        const c = camRef.current;
        driveCam({ ...c, x: g.startCam.x + (s.x - g.startScreen.x), y: g.startCam.y + (s.y - g.startScreen.y) });
      } else if (g.kind === 'move') {
        let dx = world.x - g.startWorld.x;
        let dy = world.y - g.startWorld.y;
        if (!g.moved && Math.abs(dx) + Math.abs(dy) > 1) g.moved = true;
        // Shift constrains movement to the dominant axis (works with Alt-drag duplicate — same gesture).
        // The locked axis is then excluded from snapping below (so a snap can't nudge it off the lock).
        let lockX = false; // x is FIXED (only vertical movement)
        let lockY = false; // y is FIXED (only horizontal movement)
        if (e.shiftKey) {
          if (Math.abs(dx) >= Math.abs(dy)) {
            dy = 0;
            lockY = true;
          } else {
            dx = 0;
            lockX = true;
          }
        }
        // Soft-snap the dragged group's bounding box to other elements' edges/centres (with guides). Hold
        // Cmd/Ctrl to disable snapping — NOT Alt, which is the duplicate modifier, so an Alt-drag (duplicate)
        // still snaps. Gated on g.moved so a sub-threshold jitter of a resting element can't apply a snap
        // correction that then never commits (it would revert on reload).
        const ids = Object.keys(g.originals);
        if (g.moved && !e.metaKey && !e.ctrlKey) {
          const movingSet = new Set(ids);
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          for (const id of ids) {
            const el = opsRef.current.byId(id);
            const o = g.originals[id];
            if (!el || !SNAP_KINDS.has(el.kind)) continue; // skip connectors (derived 0×0 box at the origin)
            minX = Math.min(minX, o.x);
            minY = Math.min(minY, o.y);
            maxX = Math.max(maxX, o.x + el.w);
            maxY = Math.max(maxY, o.y + el.h);
          }
          if (minX !== Infinity) {
            const moving = { x: minX + dx, y: minY + dy, w: maxX - minX, h: maxY - minY };
            const others = opsRef.current.ref.current.filter((el) => !movingSet.has(el.id) && SNAP_KINDS.has(el.kind));
            const snap = computeSnap(moving, others, SNAP_PX / camRef.current.zoom);
            if (!lockX) dx += snap.dx;
            if (!lockY) dy += snap.dy;
            // A vertical guide (axis 'x') reflects an x-snap, a horizontal guide (axis 'y') a y-snap — drop
            // the guide for a locked (un-snapped) axis so it isn't shown for a correction we didn't apply.
            const guides = snap.guides.filter((gl) => (gl.axis === 'x' ? !lockX : !lockY));
            guidesRef.current?.show(guides.map((gl) => guideToScreen(camRef.current, gl)));
          }
        } else {
          guidesRef.current?.clear();
        }
        // IMPERATIVE move: translate the dragged DOM nodes directly (compositor), offset the connectors/ink
        // via Pixi, and slide the selection chrome — WITHOUT setElements. This skips the whole per-frame
        // cascade (sort, virtualization, full Pixi rebuild, React reconcile) that made drags crawl; state is
        // committed ONCE on drop. The transform template MUST match ElementView's exactly (translate then
        // rotate, same origin) so the drop reconcile writes an identical value (no snap-back).
        // Build the node map, re-querying each frame UNTIL COMPLETE — freshly-created clones (Alt-drag) may
        // not be committed to the DOM on the first frame, and caching an incomplete map would leave them
        // un-dragged (the second back-to-back Alt-drag "did nothing" bug). Self-corrects on the next frame.
        const idCount = Object.keys(g.originals).length;
        if (!g.nodes || g.nodes.size < idCount) {
          if (!g.nodes) g.nodes = new Map();
          worldDomRef.current?.querySelectorAll<HTMLElement>('[data-elid]').forEach((n) => {
            const id = n.dataset.elid;
            if (id && g.originals[id] && !g.nodes!.has(id)) {
              n.style.willChange = 'transform'; // promote to a compositor layer for the drag (cleared on drop/abort)
              g.nodes!.set(id, n);
            }
          });
        }
        g.dx = dx;
        g.dy = dy;
        for (const id of ids) {
          const o = g.originals[id];
          const node = g.nodes.get(id);
          if (node) node.style.transform = `translate(${o.x + dx}px, ${o.y + dy}px)${o.rotation ? ` rotate(${o.rotation}deg)` : ''}`;
        }
        pixiRef.current?.setDragOffset(new Set(ids), dx, dy);
        if (selWrapRef.current) selWrapRef.current.style.transform = `translate(${dx * camRef.current.zoom}px, ${dy * camRef.current.zoom}px)`;
      } else if (g.kind === 'resize') {
        // Rotated elements resize in their local frame (opposite corner pinned) so the handles track the
        // rotation; unrotated is the plain world-axis path. Alt resizes symmetrically about the centre.
        const r = g.prev.rotation ? resizeRotated(g.prev, g.handle, world, g.mode, e.altKey) : computeResize(g.prev, g.handle, world, g.mode, e.altKey);
        if (g.scaleFont) {
          opsRef.current.patchLocal(g.id, { x: r.x, y: r.y, w: r.w, h: r.h, style: { ...g.origStyle, fontSize: Math.max(6, Math.round(g.origFont * r.scale)) } });
        } else if (g.fitFont) {
          // Sticky edge-resize: shrink the font (down to MIN_FONT) so the wrapped text fits the new box,
          // capped at the font it started the drag with so enlarging restores the original size.
          const innerW = r.w * (1 - 2 * STICKY_PAD_FRAC);
          const innerH = r.h - 2 * STICKY_PAD_FRAC * r.w;
          const el = opsRef.current.byId(g.id);
          const fontSize = fitStickyFont(el?.content ?? '', innerW, innerH, g.origFont, g.origStyle.bold);
          opsRef.current.patchLocal(g.id, { x: r.x, y: r.y, w: r.w, h: r.h, style: { ...g.origStyle, fontSize } });
        } else if (g.widthOnly) {
          // Only the width (and x, for a west handle) — the card's height stays content-driven via autoHeight.
          opsRef.current.liveGeometry(g.id, { x: r.x, w: r.w });
        } else {
          opsRef.current.liveGeometry(g.id, { x: r.x, y: r.y, w: r.w, h: r.h });
        }
        // Skip the full per-frame Pixi rebuild (its O(connectors²) line-jump pass is the resize lag): the
        // element resizes via React (one node), but connectors touching it are redrawn directly from the
        // now-live box (0 offset), and syncElements early-outs. Cleared on drop (top of up()).
        pixiRef.current?.setDragOffset(new Set([g.id]), 0, 0);
      } else if (g.kind === 'groupResize') {
        g.moved = true;
        // Proportional (aspect-locked) scale of the whole group about the opposite corner, applied UNIFORMLY
        // to every member (position + size + label font) so the arrangement is preserved. The group scale is
        // floored so the SMALLEST member never drops below MIN_SIZE (a per-member floor would let gaps keep
        // compressing and members overlap). Freehand rides along by translation only; connectors follow.
        let s = computeResize(g.bounds, g.handle, world, 'aspect', e.altKey).scale;
        let minDim = Infinity;
        for (const id of Object.keys(g.originals)) {
          const o = g.originals[id];
          if (RESIZABLE_KINDS.has(o.kind)) minDim = Math.min(minDim, o.w, o.h);
        }
        if (minDim !== Infinity && minDim > 0) s = Math.max(s, MIN_SIZE / minDim);
        // Alt scales about the group CENTRE; otherwise about the corner opposite the dragged handle.
        const ax = e.altKey ? g.bounds.x + g.bounds.w / 2 : g.handle.includes('w') ? g.bounds.x + g.bounds.w : g.bounds.x;
        const ay = e.altKey ? g.bounds.y + g.bounds.h / 2 : g.handle.includes('n') ? g.bounds.y + g.bounds.h : g.bounds.y;
        for (const id of Object.keys(g.originals)) {
          const o = g.originals[id];
          if (o.kind === 'connector') continue;
          const nx = ax + (o.x - ax) * s;
          const ny = ay + (o.y - ay) * s;
          if (RESIZABLE_KINDS.has(o.kind)) {
            const geo = { x: nx, y: ny, w: o.w * s, h: o.h * s };
            if (FONT_KINDS.has(o.kind)) {
              const baseFont = o.style?.fontSize ?? (o.kind === 'text' || o.kind === 'sticky' ? 24 : 16);
              opsRef.current.patchLocal(id, { ...geo, style: { ...o.style, fontSize: Math.max(6, Math.round(baseFont * s)) } });
            } else {
              opsRef.current.liveGeometry(id, geo);
            }
          } else {
            opsRef.current.liveGeometry(id, { x: nx, y: ny }); // freehand: translate only
          }
        }
        // Skip the full per-frame Pixi rebuild; redraw only the group's connectors from their live boxes.
        pixiRef.current?.setDragOffset(new Set(Object.keys(g.originals)), 0, 0);
      } else if (g.kind === 'rotate') {
        let deg = g.prevRotation + ((angleTo(g.center, world) - g.startAngle) * 180) / Math.PI;
        if (e.shiftKey) deg = Math.round(deg / 15) * 15;
        opsRef.current.liveGeometry(g.id, { rotation: deg });
        // A rotate can also carry connectors (endpoints move as the box rotates) — skip the full rebuild.
        pixiRef.current?.setDragOffset(new Set([g.id]), 0, 0);
      } else if (g.kind === 'marquee') {
        const s = screenFromEvent(e);
        const rect = normRect(g.startScreen.x, g.startScreen.y, s.x, s.y);
        setMarquee(rect);
        const w0 = screenToWorld(camRef.current, rect.x, rect.y);
        const w1 = screenToWorld(camRef.current, rect.x + rect.w, rect.y + rect.h);
        const wr = { x: w0.x, y: w0.y, w: w1.x - w0.x, h: w1.y - w0.y };
        const inside = opsRef.current.ref.current.filter((el) => rectContains(wr, el) && !locksRef.current.has(el.id)).map((el) => el.id);
        // Only setSelected when the enclosed SET changes (not every frame): setSelected re-renders every
        // newly-selected ElementView + re-syncs the GPU layer, so calling it per frame while sweeping a
        // marquee across many elements is the marquee lag. `inside` order is stable (elements ref is fixed
        // during the marquee), so a joined key is a cheap change-check.
        const key = inside.join(',');
        if (key !== g.lastKey) {
          g.lastKey = key;
          setSelected(Array.from(new Set([...g.base, ...inside])));
        }
      } else if (g.kind === 'pen') {
        // Capture every coalesced sub-event (a stylus reports many between frames) with its pressure.
        const coalesced = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
        const evs = coalesced.length ? coalesced : [e];
        // Densify to ~a few screen px between points so a FAST stroke (few native samples) still renders
        // as a smooth ribbon at any zoom, not a string of beads — independent of Safari's event rate.
        const stepWorld = 2.5 / Math.max(0.15, camRef.current.zoom);
        for (const ev of evs) {
          const s = screenFromEvent(ev);
          const w = screenToWorld(camRef.current, s.x, s.y);
          const raw = clampPressure((ev.pressure ?? 0.5) || 0.5);
          const prev = g.stroke[g.stroke.length - 1];
          // EMA-smooth pressure lightly (clip spikes, keep the expressive signal).
          const pr = prev ? prev[2] * PEN_PRESSURE_SMOOTH + raw * (1 - PEN_PRESSURE_SMOOTH) : raw;
          if (prev) {
            const dx = w.x - prev[0];
            const dy = w.y - prev[1];
            const segs = Math.min(48, Math.floor(Math.hypot(dx, dy) / stepWorld));
            for (let k = 1; k < segs; k++) {
              const t = k / segs;
              g.stroke.push([prev[0] + dx * t, prev[1] + dy * t, prev[2] + (pr - prev[2]) * t]);
            }
          }
          g.stroke.push([w.x, w.y, pr]);
        }
        schedulePenDraft();
      } else if (g.kind === 'connector') {
        const from = opsRef.current.byId(g.fromId);
        const tgt = [...opsRef.current.ref.current]
          .filter((el) => CONNECTABLE.has(el.kind) && el.id !== g.fromId)
          .sort((a, b) => b.z_index - a.z_index)
          .find((el) => hitTest(el, world));
        setConnectTargetId(tgt ? tgt.id : null);
        if (from) {
          // Preview the real connector: respect the grabbed start edge (nub/border) and, over a target,
          // the cursor's spot on its perimeter — so the draft matches what the drop will create.
          const fromAnchor = g.fromEdge ? perimeterAnchor(from, g.startWorld) : undefined;
          const toAnchor = tgt ? perimeterAnchor(tgt, world) : undefined;
          setDraft({ kind: 'connector', from, to: world, toBox: tgt ?? undefined, fromAnchor, toAnchor, color: '#868e96' });
        }
      } else if (g.kind === 'create') {
        setDraft({ kind: 'create', rect: normRect(g.startWorld.x, g.startWorld.y, world.x, world.y) });
      } else if (g.kind === 'place') {
        const s = screenFromEvent(e);
        if (!g.moved && Math.abs(s.x - g.startScreen.x) + Math.abs(s.y - g.startScreen.y) > 5) g.moved = true;
        if (g.moved) {
          const def = DEFAULTS[g.tool] ?? { w: 160, h: 120 };
          setDraft({ kind: 'create', rect: { x: world.x - def.w / 2, y: world.y - def.h / 2, w: def.w, h: def.h } });
        }
      } else if (g.kind === 'waypoint') {
        const wp = (g.origStyle.waypoints ?? []).map((p) => ({ ...p }));
        if (wp[g.index]) {
          wp[g.index] = { x: world.x, y: world.y };
          opsRef.current.patchLocal(g.id, { style: { ...g.origStyle, waypoints: wp } });
        }
      } else if (g.kind === 'segment') {
        // Require a real drag (a tap/pen-jitter under the threshold must not pin the rail or drop a bend,
        // nor push a no-op undo/realtime write). Screen-space so it's zoom-independent.
        if (!g.added) {
          const s = screenFromEvent(e);
          if (Math.abs(s.x - g.startScreen.x) + Math.abs(s.y - g.startScreen.y) <= 3) return;
          g.added = true;
        }
        if (g.orientation === 'free') {
          // Straight/curved: the midpoint drags the connector's ONE bend — replace the waypoints with a
          // single point at the cursor rather than dropping a fresh point on every drag.
          opsRef.current.patchLocal(g.id, { style: { ...g.origStyle, waypoints: [{ x: world.x, y: world.y }] } });
        } else if (g.orientation === 'railh' || g.orientation === 'railv') {
          // Auto-routed elbow rail: MOVE the single middle segment (pin it via elbowMid) — no anchor added.
          // railh pins y (a horizontal rail), railv pins x. Soft-snap it to other elements' lines (+guide),
          // unless Alt is held.
          const isY = g.orientation === 'railh';
          let v = isY ? world.y : world.x;
          if (!e.metaKey && !e.ctrlKey) {
            const others = opsRef.current.ref.current.filter((el) => el.id !== g.id && SNAP_KINDS.has(el.kind));
            const r = snapRail(isY ? 'y' : 'x', v, others, SNAP_PX / camRef.current.zoom);
            v = r.v;
            guidesRef.current?.show(r.guide ? [guideToScreen(camRef.current, r.guide)] : []);
          } else {
            guidesRef.current?.clear();
          }
          opsRef.current.patchLocal(g.id, { style: { ...g.origStyle, elbowMid: { axis: isY ? 'y' : 'x', v } } });
        } else {
          // Legacy elbow (explicit waypoints): move that orthogonal segment by inserting its corner.
          const base = (g.origStyle.waypoints ?? []).map((p) => ({ ...p }));
          const pos = g.orientation === 'v' ? { x: world.x, y: g.mid.y } : { x: g.mid.x, y: world.y };
          base.splice(g.controlIndex, 0, pos);
          opsRef.current.patchLocal(g.id, { style: { ...g.origStyle, waypoints: base } });
        }
      } else if (g.kind === 'label') {
        // Slide the label along the connector: project the cursor onto the drawn path → arc-length t.
        const t = projectToPolylineT(g.samplePts, world);
        if (!g.moved && Math.abs(t - (g.origStyle.labelT ?? 0.5)) > 0.001) g.moved = true;
        opsRef.current.patchLocal(g.id, { style: { ...g.origStyle, labelT: t } });
      } else if (g.kind === 'endpoint') {
        const otherId = g.which === 'from' ? g.origToId : g.origFromId;
        const origAttached = g.which === 'from' ? g.origFromId : g.origToId;
        // A shape under the cursor (not the OTHER endpoint's shape) → re-attach there; otherwise keep
        // it on the element it's already on and SLIDE along that edge. Either way we PIN a perimeter
        // anchor at the cursor (soft-snapped to the edge centre), so the end stays put as shapes move.
        const tgt = [...opsRef.current.ref.current]
          .filter((el) => CONNECTABLE.has(el.kind) && el.id !== otherId)
          .sort((a, b) => b.z_index - a.z_index)
          .find((el) => hitTest(el, world));
        const host = tgt ?? (origAttached ? opsRef.current.byId(origAttached) : undefined);
        if (!host) return;
        setConnectTargetId(tgt && tgt.id !== origAttached ? tgt.id : null);
        const anchor = perimeterAnchor(host, world);
        const style = g.which === 'from' ? { ...g.origStyle, from_anchor: anchor } : { ...g.origStyle, to_anchor: anchor };
        opsRef.current.patchLocal(g.id, g.which === 'from' ? { from_id: host.id, style } : { to_id: host.id, style });
      }
    };

    // Camera pan velocity (screen px/frame) for a cursor at (clientX, clientY): non-zero only inside the
    // EDGE_PAN_ZONE band, ramping from 0 at the band's inner edge to EDGE_PAN_MAX at (or past) the viewport
    // edge. cam.x/y are screen-space offsets, so a right/bottom edge DECREASES them (view scrolls that way).
    const edgePanVelocity = (clientX: number, clientY: number) => {
      const r = viewportRef.current?.getBoundingClientRect();
      const w = r?.width ?? window.innerWidth;
      const h = r?.height ?? window.innerHeight;
      const x = clientX - (r?.left ?? 0);
      const y = clientY - (r?.top ?? 0);
      const ramp = (d: number) => Math.min(1, Math.max(0, d) / EDGE_PAN_ZONE) * EDGE_PAN_MAX;
      let vx = 0;
      let vy = 0;
      if (x < EDGE_PAN_ZONE) vx = ramp(EDGE_PAN_ZONE - x);
      else if (x > w - EDGE_PAN_ZONE) vx = -ramp(x - (w - EDGE_PAN_ZONE));
      if (y < EDGE_PAN_ZONE) vy = ramp(EDGE_PAN_ZONE - y);
      else if (y > h - EDGE_PAN_ZONE) vy = -ramp(y - (h - EDGE_PAN_ZONE));
      return { vx, vy };
    };

    // Self-scheduling loop: while a drag's cursor sits in the edge zone, pan the camera and re-run the
    // drag with the (stationary) last cursor point so the element/connector keeps tracking the cursor.
    const edgePanStep = () => {
      edgePanRafRef.current = null;
      const g = gesture.current;
      const e = dragEventRef.current;
      if (!e || !EDGE_PAN_KINDS.has(g.kind)) return;
      const { vx, vy } = edgePanVelocity(e.clientX, e.clientY);
      if (vx === 0 && vy === 0) return; // cursor left the edge band → stop
      const c = camRef.current;
      driveCam({ ...c, x: c.x + vx, y: c.y + vy });
      processMove(e);
      edgePanRafRef.current = requestAnimationFrame(edgePanStep);
    };

    // Coalesce pointermoves to one process/frame for the setElements-heavy gestures; pan + pen run inline.
    const move = (e: PointerEvent) => {
      if (activePointerRef.current !== null && e.pointerId !== activePointerRef.current) return; // ignore non-owner pointers (palm)
      const g = gesture.current;
      if (g.kind === 'idle') return;
      if (g.kind === 'pan' || g.kind === 'pen') {
        processMove(e);
        return;
      }
      lastMoveEventRef.current = e;
      if (moveRafRef.current == null) {
        moveRafRef.current = requestAnimationFrame(() => {
          moveRafRef.current = null;
          const ev = lastMoveEventRef.current;
          lastMoveEventRef.current = null;
          if (ev) processMove(ev);
        });
      }
      // Edge auto-pan for draggable gestures: remember this point and start the pan loop when in the edge
      // band (the loop keeps panning even if the cursor then holds still at the edge). A `move` gesture
      // only qualifies once it's actually dragging, so clicking to select an element near the edge (or a
      // tiny jitter) doesn't start panning.
      if (EDGE_PAN_KINDS.has(g.kind)) {
        dragEventRef.current = e;
        const dragging = g.kind === 'move' ? g.moved : true;
        const { vx, vy } = edgePanVelocity(e.clientX, e.clientY);
        if (dragging && (vx !== 0 || vy !== 0) && edgePanRafRef.current == null) edgePanRafRef.current = requestAnimationFrame(edgePanStep);
      }
    };

    /** Flush a pending coalesced move NOW (on pointerup, so the final drag frame is applied before commit). */
    const flushMove = () => {
      if (moveRafRef.current != null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
      }
      const ev = lastMoveEventRef.current;
      lastMoveEventRef.current = null;
      if (ev) processMove(ev);
    };

    const up = (e: PointerEvent) => {
      if (activePointerRef.current !== null && e.pointerId !== activePointerRef.current) return; // a non-owner lifting (e.g. palm) doesn't end the gesture
      if (edgePanRafRef.current != null) {
        cancelAnimationFrame(edgePanRafRef.current);
        edgePanRafRef.current = null;
        setCam(camRef.current); // commit any edge-pan camera drift to React state
      }
      dragEventRef.current = null;
      flushMove();
      setInteracting(false);
      setPanning(false);
      activeMoveIdsRef.current = null;
      pixiRef.current?.setDragOffset(null, 0, 0); // end any move/resize/rotate Pixi-skip → the next sync fully rebuilds
      activePointerRef.current = null;
      if (penRafRef.current != null) {
        cancelAnimationFrame(penRafRef.current);
        penRafRef.current = null;
      }
      const g = gesture.current;
      gesture.current = { kind: 'idle' };
      guidesRef.current?.clear(); // drop any alignment guides when the gesture ends
      if (g.kind === 'pan') {
        setCam(camRef.current); // final React sync after an imperative pan
      } else if (g.kind === 'move') {
        // The Pixi drag was already cleared at the top of up(); reset the selection wrapper + layer hints.
        if (selWrapRef.current) selWrapRef.current.style.transform = '';
        g.nodes?.forEach((n) => (n.style.willChange = '')); // drop the compositor-layer hint
        if (!g.moved) {
          // A click / tiny jitter: no commit. One reconcile re-syncs the Pixi scene (dragRef now null) and
          // rewrites any ~0-delta inline node transforms back to the element's real position.
          opsRef.current.setElements(opsRef.current.ref.current.slice());
        } else {
        // The drag was IMPERATIVE (no setElements), so byId x/y are still PRE-move — build the commit from
        // originals + the final delta g.dx/g.dy (w/h/rotation are move-invariant, safe to read from byId).
        // commitGeometry runs FIRST: it syncs ref.current to the final positions (so the lazy-pin below reads
        // final endpoints) and its reconcile rewrites each ElementView transform to the SAME string we set
        // imperatively (no snap-back).
        const changes = Object.entries(g.originals)
          .map(([id, o]) => {
            const el = opsRef.current.byId(id);
            if (!el) return null;
            return {
              id,
              prev: { x: o.x, y: o.y, w: el.w, h: el.h, rotation: el.rotation ?? 0 },
              next: { x: o.x + g.dx, y: o.y + g.dy, w: el.w, h: el.h, rotation: el.rotation ?? 0 },
            };
          })
          .filter(Boolean) as { id: string; prev: Geometry; next: Geometry }[];
        opsRef.current.commitGeometry(changes);
        // Freeze any AUTO-FACING connector on a shape we just moved onto the edge it was on before the
        // move, so it keeps that orientation instead of re-snapping to a new facing edge. Uses the
        // PRE-move boxes (from `originals`) so the pinned anchor reflects where the end actually was.
        // Persisted without an undo entry (a side effect of the move, not a separate action).
        const movingIds = new Set(Object.keys(g.originals));
        const preBox = (el: CanvasElement) => (movingIds.has(el.id) ? { x: g.originals[el.id].x, y: g.originals[el.id].y, w: el.w, h: el.h } : el);
        for (const c of opsRef.current.ref.current) {
          if (c.kind !== 'connector') continue;
          const involved = (!!c.from_id && movingIds.has(c.from_id)) || (!!c.to_id && movingIds.has(c.to_id));
          if (!involved) continue;
          const from = c.from_id ? opsRef.current.byId(c.from_id) : undefined;
          const to = c.to_id ? opsRef.current.byId(c.to_id) : undefined;
          if (!from || !to) continue;
          // Freeze BOTH not-yet-pinned ends at their PRE-move facing edges, so moving one shape doesn't let
          // the connector re-orient on EITHER side — the stationary end would otherwise still auto-face
          // toward the moved shape's new position. Anchors reflect where each end sat before the move.
          const needFrom = !c.style?.from_anchor;
          const needTo = !c.style?.to_anchor;
          if (!needFrom && !needTo) continue;
          const pg = connectorGeometry(preBox(from), preBox(to), c.style ?? {});
          const style: ElementStyle = { ...c.style };
          if (needFrom) style.from_anchor = perimeterAnchor(preBox(from), pg.start);
          if (needTo) style.to_anchor = perimeterAnchor(preBox(to), pg.end);
          opsRef.current.updateProps(c.id, { style }, { record: false });
        }
        }
      } else if (g.kind === 'resize') {
        const el = opsRef.current.byId(g.id);
        if (el) {
          opsRef.current.commitGeometry([
            { id: g.id, prev: g.prev, next: geomOf(el), ...(g.scaleFont || g.fitFont ? { prevStyle: g.origStyle, nextStyle: el.style } : {}) },
          ]);
        }
      } else if (g.kind === 'groupResize') {
        if (!g.moved) return; // a click on a group handle (no drag) shouldn't record a no-op resize
        const changes = Object.keys(g.originals)
          .map((id) => {
            const o = g.originals[id];
            if (o.kind === 'connector') return null;
            const el = opsRef.current.byId(id);
            if (!el) return null;
            const styled = FONT_KINDS.has(o.kind); // font was scaled → capture style so undo restores it
            return { id, prev: { x: o.x, y: o.y, w: o.w, h: o.h, rotation: o.rotation }, next: geomOf(el), ...(styled ? { prevStyle: o.style, nextStyle: el.style } : {}) };
          })
          .filter(Boolean) as { id: string; prev: Geometry; next: Geometry; prevStyle?: ElementStyle; nextStyle?: ElementStyle }[];
        if (changes.length) opsRef.current.commitGeometry(changes);
      } else if (g.kind === 'rotate') {
        const el = opsRef.current.byId(g.id);
        if (el) opsRef.current.commitGeometry([{ id: g.id, prev: { ...geomOf(el), rotation: g.prevRotation }, next: geomOf(el) }]);
      } else if (g.kind === 'waypoint' || g.kind === 'segment') {
        if (g.kind === 'segment' && !g.added) return;
        const el = opsRef.current.byId(g.id);
        if (el) opsRef.current.updateProps(g.id, { style: el.style });
      } else if (g.kind === 'label') {
        if (!g.moved) return; // a click on the label (no drag) doesn't record a move
        const el = opsRef.current.byId(g.id);
        if (el) opsRef.current.updateProps(g.id, { style: el.style });
      } else if (g.kind === 'endpoint') {
        setConnectTargetId(null);
        const el = opsRef.current.byId(g.id);
        // A bare click (no drag) never ran the move handler, so from/to/anchor are unchanged — skip the
        // commit so it doesn't push a no-op onto the undo stack or broadcast a redundant realtime update.
        const touched =
          !!el &&
          (el.from_id !== g.origFromId ||
            el.to_id !== g.origToId ||
            el.style?.from_anchor !== g.origStyle.from_anchor ||
            el.style?.to_anchor !== g.origStyle.to_anchor);
        if (el && touched) opsRef.current.updateProps(g.id, { from_id: el.from_id, to_id: el.to_id, style: el.style });
      } else if (g.kind === 'place') {
        setDraft(null);
        const overToolbar = (e.target as HTMLElement | null)?.closest?.('[data-toolbar]');
        if (g.moved && !overToolbar) {
          const world = worldFromEvent(e);
          const def = DEFAULTS[g.tool] ?? { w: 160, h: 120 };
          spawn(g.tool, { x: world.x - def.w / 2, y: world.y - def.h / 2, w: def.w, h: def.h });
        }
      } else if (g.kind === 'marquee') {
        setMarquee(null);
      } else if (g.kind === 'pen') {
        setDraft(null);
        commitPen(g.stroke); // commits even a 1-point tap (dropping short strokes caused "skips")
      } else if (g.kind === 'connector') {
        setDraft(null);
        setConnectTargetId(null);
        const world = worldFromEvent(e);
        const s = screenFromEvent(e);
        const moved = Math.abs(s.x - g.startScreen.x) + Math.abs(s.y - g.startScreen.y) > 5;
        const target = [...opsRef.current.ref.current]
          .filter((el) => CONNECTABLE.has(el.kind) && el.id !== g.fromId)
          .sort((a, b) => b.z_index - a.z_index)
          .find((el) => hitTest(el, world));
        if (target) {
          // Respect where the user drew it (matching the live preview): pin the START edge for a nub/border
          // drag (g.fromEdge — g.startWorld is on the border, so perimeterAnchor picks the right edge), and
          // pin the END to the spot on the target where they dropped. The connector-TOOL path (not fromEdge)
          // leaves the start AUTO-FACING (an interior click has no meaningful start edge).
          const fromEl = opsRef.current.byId(g.fromId);
          const fromAnchor = g.fromEdge && fromEl ? perimeterAnchor(fromEl, g.startWorld) : undefined;
          const toAnchor = perimeterAnchor(target, world);
          const el = opsRef.current.create({
            kind: 'connector',
            x: 0,
            y: 0,
            w: 0,
            h: 0,
            from_id: g.fromId,
            to_id: target.id,
            color: '#868e96',
            style: { connectorType: 'curved', lineStyle: 'solid', arrowEnd: true, strokeWidth: 2, ...(fromAnchor ? { from_anchor: fromAnchor } : {}), to_anchor: toAnchor },
          });
          setSelected([el.id]);
        } else if (g.fromEdge && !moved) {
          setSelected([g.fromId]); // a click on the border (no drag) just selects the shape
        }
      } else if (g.kind === 'create') {
        setDraft(null);
        const world = worldFromEvent(e);
        const dragged = normRect(g.startWorld.x, g.startWorld.y, world.x, world.y);
        const def = DEFAULTS[g.tool] ?? { w: 160, h: 120 };
        const big = dragged.w > 6 && dragged.h > 6;
        const rect = big
          ? { x: dragged.x, y: dragged.y, w: Math.max(MIN_SIZE, dragged.w), h: Math.max(MIN_SIZE, dragged.h) }
          : { x: g.startWorld.x, y: g.startWorld.y, w: def.w, h: def.h };
        spawn(g.tool, rect);
      }
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up); // iOS fires this on interruption — commit what we have, don't strand the stroke
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (moveRafRef.current != null) cancelAnimationFrame(moveRafRef.current);
      if (edgePanRafRef.current != null) cancelAnimationFrame(edgePanRafRef.current);
    };
  }, [screenFromEvent, worldFromEvent, driveCam, setCam, setSelected, setTool, spawn, commitPen, penMeta]);

  // ---- element actions (color / font / z-order / duplicate / delete) ----
  const applyColor = useCallback(
    (c: string) => {
      setColor(c);
      selectedRef.current.forEach((id) => {
        const el = opsRef.current.byId(id);
        if (el && el.kind !== 'image') opsRef.current.updateProps(id, { color: c });
      });
    },
    [],
  );
  const applyFont = useCallback((delta: number) => {
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (!el) return;
      const size = Math.max(8, Math.min(200, (el.style?.fontSize ?? 16) + delta));
      opsRef.current.updateProps(id, { style: { ...el.style, fontSize: size } });
    });
  }, []);
  const duplicate = useCallback(() => {
    const ids: string[] = [];
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (!el) return;
      const copy = opsRef.current.create({
        kind: el.kind,
        x: el.x + 16,
        y: el.y + 16,
        w: el.w,
        h: el.h,
        rotation: el.rotation,
        color: el.color,
        content: el.content,
        from_id: el.from_id,
        to_id: el.to_id,
        style: el.style,
      });
      ids.push(copy.id);
    });
    if (ids.length) setSelected(ids);
  }, [setSelected]);
  const deleteSelected = useCallback(() => {
    const ids = selectedRef.current;
    if (!ids.length) return;
    opsRef.current.remove(ids);
    setSelected([]);
    setEditingId(null);
  }, [setSelected]);

  // ---- connector context toolbar ----
  const setConnectorStyle = useCallback((patch: Partial<ElementStyle>) => {
    const id = selectedRef.current[0];
    const el = opsRef.current.byId(id);
    if (el) opsRef.current.updateProps(id, { style: { ...el.style, ...patch } });
  }, []);
  const reverseConnector = useCallback(() => {
    const id = selectedRef.current[0];
    const el = opsRef.current.byId(id);
    if (el && el.kind === 'connector') opsRef.current.updateProps(id, { from_id: el.to_id, to_id: el.from_id });
  }, []);
  const editConnectorLabel = useCallback(
    (id: string) => {
      const el = opsRef.current.byId(id);
      if (el && el.kind === 'connector') {
        setSelected([id]);
        setEditingId(id);
      }
    },
    [setSelected],
  );

  // ---- object edits (bold / align / lock / clear / clipboard) ----
  const applyBold = useCallback(() => {
    const anyBold = selectedRef.current.some((id) => opsRef.current.byId(id)?.style?.bold);
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (el) opsRef.current.updateProps(id, { style: { ...el.style, bold: !anyBold } });
    });
  }, []);
  const applyAlign = useCallback((a: 'left' | 'center' | 'right') => {
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (el) opsRef.current.updateProps(id, { style: { ...el.style, textAlign: a } });
    });
  }, []);
  const applyVAlign = useCallback((a: 'top' | 'middle' | 'bottom') => {
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (el) opsRef.current.updateProps(id, { style: { ...el.style, vAlign: a } });
    });
  }, []);
  const toggleLock = useCallback(() => {
    const allLocked = selectedRef.current.every((id) => opsRef.current.byId(id)?.style?.locked);
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (el) opsRef.current.updateProps(id, { style: { ...el.style, locked: !allLocked } });
    });
  }, []);
  const clearContent = useCallback(() => {
    selectedRef.current.forEach((id) => {
      const el = opsRef.current.byId(id);
      if (el && el.content) opsRef.current.updateProps(id, { content: '' });
    });
  }, []);
  const copySelection = useCallback(() => {
    clipboardRef.current = selectedRef.current
      .map((id) => opsRef.current.byId(id))
      .filter((el): el is CanvasElement => !!el && el.kind !== 'connector');
  }, []);

  // Copy a link that reopens the board centred on the current selection (or the current view) at this
  // zoom — so whoever opens it lands near the same element, seeing what I see.
  const copyLinkToSelection = useCallback(() => {
    const geom = selectedRef.current
      .map((id) => opsRef.current.byId(id))
      .filter((el): el is CanvasElement => !!el && el.kind !== 'connector' && el.kind !== 'freehand');
    const b = geom.length ? boundsOf(geom) : null;
    const r = viewportRef.current?.getBoundingClientRect();
    const vp = { w: r?.width ?? window.innerWidth, h: r?.height ?? window.innerHeight };
    const center = b ? { x: b.x + b.w / 2, y: b.y + b.h / 2 } : screenToWorld(camRef.current, vp.w / 2, vp.h / 2);
    const url = `${window.location.origin}${window.location.pathname}?${viewParams(camRef.current.zoom, center)}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(() => flash('Link copied — opens at this view'), () => flash('Copy failed'));
    else flash('Copy not available');
  }, [flash]);
  const paste = useCallback(
    (at?: { x: number; y: number }) => {
      const items = clipboardRef.current;
      if (!items.length) return;
      const b = boundsOf(items);
      const ox = at && b ? at.x - (b.x + b.w / 2) : 28;
      const oy = at && b ? at.y - (b.y + b.h / 2) : 28;
      const ids: string[] = [];
      items.forEach((el) => {
        const copy = opsRef.current.create({ kind: el.kind, x: el.x + ox, y: el.y + oy, w: el.w, h: el.h, rotation: el.rotation, color: el.color, content: el.content, style: el.style });
        ids.push(copy.id);
      });
      if (ids.length) setSelected(ids);
    },
    [setSelected],
  );

  // ---- right-click context menu ----
  const onContextMenu = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      const world = worldFromEvent(e);
      const hit = [...opsRef.current.ref.current]
        .filter((el) => el.kind !== 'connector' && el.kind !== 'freehand')
        .sort((a, b) => b.z_index - a.z_index)
        .find((el) => hitTest(el, world));
      let items: MenuEntry[];
      if (hit) {
        if (!selectedRef.current.includes(hit.id)) setSelected([hit.id]);
        const el = opsRef.current.byId(hit.id);
        const locked = !!el?.style?.locked;
        const textish = selectedRef.current.some((id) => {
          const x = opsRef.current.byId(id);
          return !!x && (x.kind === 'sticky' || x.kind === 'text' || x.kind === 'shape');
        });
        const placerMember = el?.created_by ? members.find((m) => m.id === el.created_by) : undefined;
        const placer = placerMember?.name;
        const editor = el?.updated_by ? members.find((m) => m.id === el.updated_by)?.name : undefined;
        items = [
          { label: 'Copy', shortcut: '⌘C', onSelect: copySelection },
          { label: 'Duplicate', icon: <Duplicate />, shortcut: '⌘D', onSelect: duplicate },
          { label: 'Copy link to selection', onSelect: copyLinkToSelection },
          'separator',
          ...(textish
            ? ([
                { label: 'Edit text', onSelect: () => { setSelected([hit.id]); setEditingId(hit.id); } },
                { label: 'Clear content', onSelect: clearContent },
                'separator' as const,
              ] as MenuEntry[])
            : []),
          {
            label: 'Arrange',
            submenu: [
              { label: 'Bring to front', icon: <ToTop />, onSelect: () => opsRef.current.reorder(selectedRef.current, 'front') },
              { label: 'Bring forward', onSelect: () => opsRef.current.reorder(selectedRef.current, 'forward') },
              { label: 'Send backward', onSelect: () => opsRef.current.reorder(selectedRef.current, 'backward') },
              { label: 'Send to back', icon: <ToBottom />, onSelect: () => opsRef.current.reorder(selectedRef.current, 'back') },
            ],
          },
          { label: locked ? 'Unlock' : 'Lock', onSelect: toggleLock },
          'separator',
          { label: 'Delete', icon: <Trash />, danger: true, shortcut: 'Del', onSelect: deleteSelected },
          ...(el
            ? ([
                'separator' as const,
                {
                  node: (
                    <div className="px-1.5 pb-1 pt-1.5">
                      <div className="mb-2 flex items-center gap-2">
                        <MemberAvatar member={placerMember ?? null} size={24} />
                        <div className="min-w-0 leading-tight">
                          <div className="truncate text-[12px] font-medium text-fg">{placer ?? 'Unknown'}</div>
                          <div className="text-[10px] uppercase tracking-wide text-fg-faint">Author</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-2.5 gap-y-1 text-[11px]">
                        <span className="text-fg-faint">Created</span>
                        <span className="truncate text-fg-soft" title={timeAgo(el.created_at)}>{fmtDateTime(el.created_at)}</span>
                        <span className="text-fg-faint">Updated</span>
                        <span className="truncate text-fg-soft" title={timeAgo(el.updated_at)}>
                          {fmtDateTime(el.updated_at)}
                          {editor && editor !== placer ? <span className="text-fg-faint"> · by {editor}</span> : null}
                        </span>
                      </div>
                    </div>
                  ),
                },
              ] as MenuEntry[])
            : []),
        ];
      } else {
        items = [
          { label: 'Paste', shortcut: '⌘V', disabled: clipboardRef.current.length === 0, onSelect: () => paste(world) },
          { label: 'Select all', shortcut: '⌘A', onSelect: () => setSelected(opsRef.current.ref.current.filter((el) => !locksRef.current.has(el.id)).map((el) => el.id)) },
          'separator',
          { label: 'Add sticky note', onSelect: () => spawn('sticky', { x: world.x - 88, y: world.y - 88, w: 176, h: 176 }) },
          {
            label: 'Add rectangle',
            onSelect: () => {
              const el = opsRef.current.create({ kind: 'shape', x: world.x - 80, y: world.y - 50, w: 160, h: 100, color: colorRef.current, style: { shape: 'rectangle' } });
              setSelected([el.id]);
            },
          },
          'separator',
          { label: 'Copy link to this view', onSelect: copyLinkToSelection },
          { label: 'Zoom to fit', onSelect: () => fitToContent(opsRef.current.ref.current) },
        ];
      }
      setMenu({ x: e.clientX, y: e.clientY, items });
    },
    [worldFromEvent, setSelected, duplicate, deleteSelected, spawn, fitToContent, members, copySelection, copyLinkToSelection, clearContent, toggleLock, paste],
  );

  // ---- imagery: upload / drag-drop / paste / url ----
  const placeImageFile = useCallback(
    (file: File, world?: { x: number; y: number }) => {
      if (!file.type.startsWith('image/')) return;
      const probe = new Image();
      const objurl = URL.createObjectURL(file);
      probe.onload = async () => {
        const scale = Math.min(1, 520 / Math.max(probe.width, probe.height));
        const w = probe.width * scale;
        const h = probe.height * scale;
        const vp = viewportRef.current;
        const c = world ?? screenToWorld(camRef.current, (vp?.clientWidth ?? 800) / 2, (vp?.clientHeight ?? 600) / 2);
        URL.revokeObjectURL(objurl);
        try {
          const el = await api.uploadImage(whiteboardId, file, { x: c.x - w / 2, y: c.y - h / 2, w, h });
          opsRef.current.mergeUpsert(el);
          setSelected([el.id]);
        } catch {
          /* storage disabled / upload failed */
        }
      };
      probe.src = objurl;
    },
    [whiteboardId, setSelected],
  );
  const placeImageUrl = useCallback(
    (src: string, world?: { x: number; y: number }) => {
      const vp = viewportRef.current;
      const c = world ?? screenToWorld(camRef.current, (vp?.clientWidth ?? 800) / 2, (vp?.clientHeight ?? 600) / 2);
      const done = (w: number, h: number) => {
        const el = opsRef.current.create({ kind: 'image', x: c.x - w / 2, y: c.y - h / 2, w, h, style: { src } });
        setSelected([el.id]);
      };
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        const scale = Math.min(1, 520 / Math.max(probe.width || 520, probe.height || 400));
        done((probe.width || 400) * scale, (probe.height || 300) * scale);
      };
      probe.onerror = () => done(280, 200);
      probe.src = src;
    },
    [setSelected],
  );
  const onPickImage = useCallback((file: File) => placeImageFile(file), [placeImageFile]);
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const s = screenFromEvent(e);
      const world = screenToWorld(camRef.current, s.x, s.y);
      const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
      if (files.length) {
        files.forEach((f, i) => placeImageFile(f, { x: world.x + i * 24, y: world.y + i * 24 }));
        return;
      }
      const uri = (e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain')).trim();
      if (/^https?:\/\//i.test(uri)) placeImageUrl(uri, world);
    },
    [screenFromEvent, placeImageFile, placeImageUrl],
  );
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  // Clipboard paste: an image file → upload; internal elements → duplicate at centre; an image URL → link.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const t = document.activeElement as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const dt = e.clipboardData;
      if (!dt) return;
      const vp = viewportRef.current;
      const world = screenToWorld(camRef.current, (vp?.clientWidth ?? 800) / 2, (vp?.clientHeight ?? 600) / 2);
      const imgItem = Array.from(dt.items).find((it) => it.kind === 'file' && it.type.startsWith('image/'));
      if (imgItem) {
        const file = imgItem.getAsFile();
        if (file) {
          e.preventDefault();
          placeImageFile(file, world);
          return;
        }
      }
      if (clipboardRef.current.length) {
        e.preventDefault();
        paste(world);
        return;
      }
      const text = dt.getData('text/plain').trim();
      if (/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?\S*)?$/i.test(text)) {
        e.preventDefault();
        placeImageUrl(text, world);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [placeImageFile, placeImageUrl, paste]);

  // ---- keyboard ----
  useEffect(() => {
    const isText = () => {
      const el = document.activeElement;
      return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        if (!isText()) spaceRef.current = true;
        return;
      }
      if (isText()) return;
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) opsRef.current.redo();
        else opsRef.current.undo();
        return;
      }
      if (meta && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        opsRef.current.redo();
        return;
      }
      if (meta && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicate();
        return;
      }
      if (meta && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelection();
        return;
      }
      if (meta && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        copySelection();
        deleteSelected();
        return;
      }
      if (meta && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelected(opsRef.current.ref.current.filter((el) => !locksRef.current.has(el.id)).map((el) => el.id));
        return;
      }
      if (meta && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        zoomButton(1.25);
        return;
      }
      if (meta && e.key === '-') {
        e.preventDefault();
        zoomButton(1 / 1.25);
        return;
      }
      if (meta && e.key === '0') {
        e.preventDefault();
        const vp = viewportRef.current;
        const cx = (vp?.clientWidth ?? 0) / 2;
        const cy = (vp?.clientHeight ?? 0) / 2;
        const c = camRef.current;
        const k = 1 / c.zoom;
        tweenCam({ zoom: 1, x: cx - (cx - c.x) * k, y: cy - (cy - c.y) * k }, 200);
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelected();
        return;
      }
      if (e.key === 'Escape') {
        if (['move', 'resize', 'groupResize', 'rotate'].includes(gesture.current.kind)) abortImperativeMove(); // reset transforms + clear the Pixi-skip
        setTool('select');
        setSelected([]);
        setEditingId(null);
        gesture.current = { kind: 'idle' };
        setDraft(null);
        setHoveredId(null);
        setConnectTargetId(null);
        guidesRef.current?.clear(); // an aborted drag leaves no stray alignment guides
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'r') {
        setShapeVariant('rectangle');
        setTool('shape');
        return;
      }
      if (k === 'o') {
        setShapeVariant('ellipse');
        setTool('shape');
        return;
      }
      const map: Record<string, Tool> = { v: 'select', h: 'pan', s: 'sticky', t: 'text', c: 'connector', p: 'pen' };
      const t = map[k];
      if (t) setTool(t);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') spaceRef.current = false;
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [duplicate, deleteSelected, setSelected, setTool, copySelection, paste, setShapeVariant, zoomButton, tweenCam]);

  const rename = useCallback(
    (name: string) => {
      setWb((w) => (w ? { ...w, name } : w));
      api.updateWhiteboard(whiteboardId, { name }).catch(() => {});
    },
    [whiteboardId],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const sorted = useMemo(() => [...els.elements].sort((a, b) => a.z_index - b.z_index), [els.elements]);
  const selectedEls = useMemo(() => els.elements.filter((e) => selectedSet.has(e.id)), [els.elements, selectedSet]);
  const singleConnector = selectedEls.length === 1 && selectedEls[0].kind === 'connector' ? selectedEls[0] : null;

  // Virtualize DOM elements: only mount stickies/text/shapes/images intersecting the viewport (+ a
  // screen-space margin so they appear just before scrolling in). Selected/editing elements always stay.
  // Ink is on the GPU and connectors are on SVG, so they're not virtualized here.
  const visibleDomEls = useMemo(() => {
    const m = 240; // screen px slack
    const tl = screenToWorld(cam, -m, -m);
    const br = screenToWorld(cam, vpSize.w + m, vpSize.h + m);
    return sorted.filter((el) => {
      if (el.kind === 'connector' || el.kind === 'freehand') return false;
      if (selectedSet.has(el.id) || editingId === el.id) return true;
      return el.x < br.x && el.x + el.w > tl.x && el.y < br.y && el.y + el.h > tl.y;
    });
  }, [sorted, cam, vpSize, selectedSet, editingId]);

  // Stable child callbacks so the memoized ElementView / ConnectorLabel don't re-render on camera changes.
  const onElementDoubleClick = useCallback(
    (id: string) => {
      setTool('select');
      setSelected([id]);
      setEditingId(id);
    },
    [setTool, setSelected],
  );
  const commitContent = useCallback((id: string, text: string) => opsRef.current.updateProps(id, { content: text }), []);

  // A sticky's geometry when its editor opened — so any auto-grow while typing is persisted as ONE
  // undo-able change when editing ends (rather than per keystroke, or not at all).
  const editGeomRef = useRef<{ id: string; prev: { x: number; y: number; w: number; h: number; rotation: number } } | null>(null);
  useEffect(() => {
    if (!editingId) return;
    const el = opsRef.current.byId(editingId);
    editGeomRef.current = el ? { id: editingId, prev: { x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation ?? 0 } } : null;
  }, [editingId]);

  // Grow a sticky to keep overflowing text visible — but NEVER shrink it and never force it square.
  // Stickies are freely resizable now, so a hand-set width/height must be respected: we only bump the
  // height up when the content genuinely needs more room than the current box. (The old code clamped to
  // max(contentPx, width), which snapped any non-square sticky back to a square the moment you edited it.)
  // Live/local; persisted on edit end.
  const autoHeight = useCallback((id: string, px: number) => {
    const el = opsRef.current.byId(id);
    if (!el) return;
    const needed = Math.round(px);
    // Item cards are content-driven: match the measured height EXACTLY (grow or shrink) so the card
    // never clips or scrolls and the selection box wraps it. Local-only — every client measures the
    // same shared item content, so sizes converge without persisting/broadcasting geometry.
    if (el.kind === 'item' || el.kind === 'search') {
      if (Math.abs(needed - el.h) >= 1) opsRef.current.liveGeometry(id, { h: needed });
      return;
    }
    if (el.kind !== 'sticky') return;
    if (needed <= el.h + 1) return; // content fits — leave the user's height alone
    opsRef.current.liveGeometry(id, { h: needed });
  }, []);

  const endEditing = useCallback(() => {
    const snap = editGeomRef.current;
    editGeomRef.current = null;
    if (snap) {
      const el = opsRef.current.byId(snap.id);
      if (el && Math.abs((el.h ?? 0) - snap.prev.h) >= 1) {
        opsRef.current.commitGeometry([{ id: snap.id, prev: snap.prev, next: { x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation ?? 0 } }]);
      }
    }
    setEditingId(null);
  }, []);

  // ---- comments ----
  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const isAdmin = me?.role === 'admin';

  const openThread = useCallback((id: string) => {
    setDraftPin(null);
    setActiveCommentId(id);
    cmRef.current.markSeen(id); // opening a thread clears its unread badge for me
  }, []);

  // Fly the camera so a thread's pin sits at the viewport centre, then open it (from the flyout).
  const flyToComment = useCallback(
    (threadId: string) => {
      const root = cmRef.current.comments.find((c) => c.id === threadId);
      if (!root) return;
      const vp = viewportRef.current;
      const W = vp?.clientWidth ?? window.innerWidth;
      const H = vp?.clientHeight ?? window.innerHeight;
      const zoom = camRef.current.zoom;
      tweenCam({ zoom, x: W / 2 - root.x * zoom, y: H / 2 - root.y * zoom }, 260);
      setDraftPin(null);
      setActiveCommentId(threadId);
      cmRef.current.markSeen(threadId);
    },
    [tweenCam],
  );

  // Deep-link: a ?comment=<id> (from a mention notification / shared link) opens that comment's thread
  // and flies to its pin once the comments have loaded, then strips the param (preserving other query +
  // the hash). Retries across comment loads until the target id is present.
  const didComment = useRef(false);
  useEffect(() => {
    if (didComment.current || loading) return;
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('comment');
    if (!cid) {
      didComment.current = true;
      return;
    }
    const target = cm.comments.find((c) => c.id === cid);
    if (!target) return; // comments may still be arriving — try again when they change
    didComment.current = true;
    flyToComment(target.parent_id ?? target.id);
    params.delete('comment');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  }, [loading, cm.comments, flyToComment]);

  // Screen anchor for the floating object toolbar: horizontal centre + top/bottom of the selection.
  const toolbarAnchor = useMemo<ToolbarAnchor | null>(() => {
    if (selectedEls.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const el of selectedEls) {
      if (el.kind === 'connector') {
        const from = el.from_id ? els.byId(el.from_id) : undefined;
        const to = el.to_id ? els.byId(el.to_id) : undefined;
        if (from && to) {
          const g = connectorGeometry(from, to, el.style ?? {});
          for (const p of [g.start, g.end, ...g.waypoints]) {
            minX = Math.min(minX, p.x);
            minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x);
            maxY = Math.max(maxY, p.y);
          }
        }
      } else {
        minX = Math.min(minX, el.x);
        minY = Math.min(minY, el.y);
        maxX = Math.max(maxX, el.x + el.w);
        maxY = Math.max(maxY, el.y + el.h);
      }
    }
    if (minX === Infinity) return null;
    const tc = worldToScreen(cam, (minX + maxX) / 2, minY);
    const bc = worldToScreen(cam, (minX + maxX) / 2, maxY);
    return { x: tc.x, top: tc.y, bottom: bc.y };
  }, [selectedEls, els, cam]);

  // The grid + world transform are driven imperatively by applyCam (see the imperative camera), so they
  // aren't in the React style below — a pan/zoom updates the DOM directly with no per-frame re-render.
  const cursorStyle = panning
    ? PANNING_CURSOR
    : tool === 'pan'
      ? PAN_CURSOR
      : tool === 'select'
        ? 'default'
        : 'crosshair';

  // The open comment thread (if it still exists) and where to anchor its popover — either the active
  // thread's pin or an un-submitted draft pin, projected world→screen.
  const activeThread = activeCommentId ? cm.threads.find((t) => t.root.id === activeCommentId) ?? null : null;
  const commentWorld = draftPin ?? (activeThread ? { x: activeThread.root.x, y: activeThread.root.y } : null);
  const commentAnchor = commentWorld ? { x: commentWorld.x * cam.zoom + cam.x, y: commentWorld.y * cam.zoom + cam.y } : null;

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-surface">
        <Spinner className="h-6 w-6 text-fg-muted" />
      </div>
    );
  }

  return (
    <CanvasItemsProvider value={itemStore}>
    <div
      ref={viewportRef}
      className="fixed inset-0 touch-none select-none overflow-hidden bg-surface"
      style={{
        cursor: cursorStyle,
        backgroundImage: 'radial-gradient(circle, var(--line) 1px, transparent 1px)',
        // backgroundSize/Position (grid) are set imperatively by applyCam.
      }}
      onPointerDown={onBackgroundPointerDown}
      onPointerMove={onViewportPointerMove}
      onContextMenu={onContextMenu}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onPointerLeave={() => {
        sendCursorRef.current(null);
        setHoveredId(null);
      }}
    >
      {/* transform is set imperatively by applyCam (never via React) so a mid-gesture re-render can't overwrite it */}
      <div ref={worldDomRef} className="absolute left-0 top-0 touch-none" style={{ transformOrigin: '0 0' }}>
        {visibleDomEls.map((el) => (
          <ElementView
            key={el.id}
            el={el}
            selected={selectedSet.has(el.id)}
            editing={editingId === el.id}
            lock={locks.get(el.id)}
            onPointerDown={onElementPointerDown}
            onDoubleClick={onElementDoubleClick}
            onContentCommit={commitContent}
            onEditEnd={endEditing}
            onAutoHeight={autoHeight}
            onSetSearchBoards={setSearchBoards}
          />
        ))}
        <OverlaySvg elements={sorted} byId={els.byId} onElementPointerDown={onElementPointerDown} onConnectorDoubleClick={editConnectorLabel} />
        <DraftLayer draft={draft} />
        {sorted
          .filter((el) => el.kind === 'connector' && (el.content || editingId === el.id))
          .map((el) => (
            <ConnectorLabel
              key={`lbl-${el.id}`}
              connector={el}
              from={el.from_id ? els.byId(el.from_id) : undefined}
              to={el.to_id ? els.byId(el.to_id) : undefined}
              editing={editingId === el.id}
              draggable={selectedSet.has(el.id)}
              onLabelDown={onLabelDown}
              onCommit={commitContent}
              onEditEnd={endEditing}
            />
          ))}
      </div>

      {/* GPU (WebGL) layer for committed ink + the live pen draft — above DOM elements, below UI/selection. */}
      <PixiInkLayer ref={pixiRef} elements={sorted} cam={cam} selectedIds={selectedSet} draft={draft} byId={els.byId} />

      {/* Wrapper is translated imperatively during an imperative move so the selection chrome tracks the
          drag without a per-frame re-render (SelectionLayer itself renders at the pre-move positions). */}
      <div ref={selWrapRef} className="pointer-events-none absolute inset-0 z-20">
        <SelectionLayer cam={cam} selected={selectedEls} marquee={marquee} onHandlePointerDown={onHandlePointerDown} />
      </div>
      <SnapGuidesLayer ref={guidesRef} />
      {/* Miro-style connection nubs on the hovered element (drag one to another shape to connect). */}
      {tool === 'select' && hoveredId && editingId !== hoveredId ? (
        <ConnectionNubs cam={cam} el={els.byId(hoveredId)} onStart={startConnectorFrom} />
      ) : null}
      {connectTargetId ? <TargetHighlight cam={cam} el={els.byId(connectTargetId)} /> : null}
      {singleConnector ? (
        <ConnectorHandles
          cam={cam}
          connector={singleConnector}
          from={singleConnector.from_id ? els.byId(singleConnector.from_id) : undefined}
          to={singleConnector.to_id ? els.byId(singleConnector.to_id) : undefined}
          onEndpointDown={onEndpointDown}
          onWaypointDown={onWaypointDown}
          onSegmentDown={onSegmentDown}
        />
      ) : null}
      <Cursors cam={cam} cursors={rt.cursors} />

      {/* Miro-like comment pins (world-anchored, screen-space overlay) + the active thread popover. */}
      <CommentPins
        cam={cam}
        threads={cm.threads}
        activeId={activeCommentId}
        membersById={membersById}
        showResolved
        unread={cm.unread}
        draft={draftPin}
        onOpen={openThread}
        onMove={(id, world) => cmRef.current.livePin(id, world.x, world.y)}
        onCommitMove={(id, world) => cmRef.current.commitPin(id, world.x, world.y)}
      />
      {commentAnchor ? (
        <CommentThreadPopover
          key={activeThread?.root.id ?? 'draft'}
          thread={activeThread}
          anchor={commentAnchor}
          members={members}
          meId={selfId}
          isAdmin={isAdmin}
          onSubmitNew={(body) => {
            if (!draftPin) return;
            const c = cmRef.current.createThread(draftPin.x, draftPin.y, body);
            setDraftPin(null);
            setActiveCommentId(c.id);
            cmRef.current.markSeen(c.id);
            setTool('select');
          }}
          onReply={(body) => {
            if (activeThread) {
              cmRef.current.reply(activeThread.root.id, body);
              cmRef.current.markSeen(activeThread.root.id); // I just posted → I've seen this thread
            }
          }}
          onReact={(commentId, emoji) => cmRef.current.toggleReaction(commentId, emoji)}
          onEdit={(id, body) => cmRef.current.edit(id, body)}
          onDelete={(id) => {
            cmRef.current.remove(id);
            if (!activeThread || id === activeThread.root.id) {
              setActiveCommentId(null);
              setDraftPin(null);
            }
          }}
          onToggleResolve={() => {
            if (activeThread) cmRef.current.toggleResolve(activeThread.root.id);
          }}
          onClose={() => {
            setActiveCommentId(null);
            setDraftPin(null);
          }}
        />
      ) : null}

      <Toolbar tool={tool} onTool={setTool} shapeVariant={shapeVariant} onShapeVariant={setShapeVariant} penSize={penSize} onPenSize={setPenSize} penMode={penMode} onPenMode={setPenMode} penColor={penColor} markerColor={markerColor} onPenColor={setPenColor} onMarkerColor={setMarkerColor} color={color} onColor={applyColor} onPickImage={onPickImage} onPlaceStart={onPlaceStart} inputMode={inputMode} onInputMode={setInputMode} onAddItemCard={() => setItemPicker({})} onAddSearchCard={() => setSearchPicker({})} />
      <TopBar
        name={wb?.name ?? 'Whiteboard'}
        onRename={rename}
        zoom={cam.zoom}
        onZoomIn={() => zoomButton(1.25)}
        onZoomOut={() => zoomButton(1 / 1.25)}
        onZoomReset={() => {
          const vp = viewportRef.current;
          const cx = (vp?.clientWidth ?? 0) / 2;
          const cy = (vp?.clientHeight ?? 0) / 2;
          const c = camRef.current;
          const k = 1 / c.zoom;
          tweenCam({ zoom: 1, x: cx - (cx - c.x) * k, y: cy - (cy - c.y) * k }, 200);
        }}
        onZoomFit={() => fitToContent(els.ref.current)}
        presence={rt.presence}
        connected={rt.connected}
        followId={followId}
        onFollow={startFollow}
        onStopFollow={stopFollow}
        onBring={bringToMe}
        canUndo={els.canUndo}
        canRedo={els.canRedo}
        onUndo={els.undo}
        onRedo={els.redo}
        comments={<CommentFlyout threads={cm.threads} members={members} unread={cm.unread} onFocus={flyToComment} />}
      />
      {interacting ? null : singleConnector ? (
        <ConnectorInspector anchor={toolbarAnchor} connector={singleConnector} onStyle={setConnectorStyle} onColor={applyColor} onReverse={reverseConnector} onAddText={() => editConnectorLabel(singleConnector.id)} onDelete={deleteSelected} />
      ) : (
        <Inspector
          selected={selectedEls}
          anchor={toolbarAnchor}
          onColor={applyColor}
          onFont={applyFont}
          onBold={applyBold}
          onAlign={applyAlign}
          onVAlign={applyVAlign}
          onReorder={(d) => els.reorder(selectedIds, d)}
          onDuplicate={duplicate}
          onLock={toggleLock}
          onDelete={deleteSelected}
          onChangeItem={() => { if (selectedEls.length === 1 && selectedEls[0].kind === 'item') setItemPicker({ retargetId: selectedEls[0].id }); }}
          onEditSearch={() => { if (selectedEls.length === 1 && selectedEls[0].kind === 'search') setSearchPicker({ retargetId: selectedEls[0].id, query: selectedEls[0].style?.query ?? '' }); }}
          onSetField={(key, on) => {
            const el = selectedEls.length === 1 ? selectedEls[0] : null;
            if (!el || (el.kind !== 'item' && el.kind !== 'search')) return;
            opsRef.current.updateProps(el.id, { style: { ...el.style, fields: { ...(el.style?.fields ?? {}), [key]: on } } }, { record: true });
          }}
        />
      )}
      {menu ? <ContextMenu open x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} /> : null}
      {flashMsg ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center">
          <div className="rounded-full bg-black/80 px-3.5 py-1.5 text-[13px] font-medium text-white shadow-lg">{flashMsg}</div>
        </div>
      ) : null}
      {typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('ptrdebug') ? <PointerDebug /> : null}
    </div>
    {/* Board Items (SBB-13/14): opened by the left-toolbar "Board Items" combo (add) or a card's floating
        toolbar (change). A retargetId re-points the existing card; otherwise a new card is placed. */}
    <ItemCardPicker
      open={!!itemPicker}
      onClose={() => setItemPicker(null)}
      onPick={(itemId, boardId) => { if (itemPicker?.retargetId) retargetItemCard(itemPicker.retargetId, itemId, boardId); else placeItemCard(itemId, boardId); }}
      store={itemStore}
      heading={itemPicker?.retargetId ? 'Change item' : 'Add item card'}
    />
    <SearchQueryPicker
      open={!!searchPicker}
      onClose={() => setSearchPicker(null)}
      onPick={(query) => { if (searchPicker?.retargetId) retargetSearchCard(searchPicker.retargetId, query); else placeSearchCard(query); }}
      initialQuery={searchPicker?.query ?? ''}
      title={searchPicker?.retargetId ? 'Edit search' : 'Add search results'}
      confirmLabel={searchPicker?.retargetId ? 'Save' : 'Add to canvas'}
    />
    </CanvasItemsProvider>
  );
}
